import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    Divider,
    Dropdown,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Tooltip,
    Typography,
    message,
} from "antd";
import {
    DeleteOutlined,
    ClockCircleOutlined,
    EditOutlined,
    EllipsisOutlined,
    ExclamationCircleOutlined,
    ExpandOutlined,
    PlusOutlined,
    SaveOutlined,
    ShrinkOutlined,
    UndoOutlined,
    UploadOutlined,
    RedoOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
} from "@ant-design/icons";
import {
    GetV2Automation,
    GetV2AutomationSteps,
    GetV2TemplatesContextList,
    ReplaceV2AutomationSteps,
    UpdateV2Automation,
    V2AutomationEdgeDto,
    V2AutomationStepDto,
    V2TemplateListItemDto,
} from "../../api/v2/automations";
import {
    CreateV2AutomationTrigger,
    EnsureTriggerExitCondition,
    GetV2AutomationTriggers,
    UpdateV2Trigger,
    UpdateV2TriggerEntryEvent,
} from "../../api/v2/triggers";
import ConditionTreeEditorModal from "./ConditionsTreeEditorModal";
import { GetConditionTreeBundle, TreeBundle } from "../../api/v2/conditionTree";
import { CreateChildGroup } from "../../api/v2/conditionTree";
import { CreateEventLeaf, DeleteConditionItem, PatchBasicCondition, PatchCondition } from "../../api/v2/conditions";
import useFlag from "../../hooks/useFlag";
import useToast from "../../hooks/useToast";
import { ToastSkin } from "../../context/ToastContext";
import V2Tag from "../../components/v2/V2Tag";
import AutomationTreeCanvas from "../../components/automations/AutomationTreeCanvas";
import StandByIcon from "../../assets/icons/stand-by-icon.png";
import ConditionIcon from "../../assets/icons/condition-icon.png";
import WaitEventIcon from "../../assets/icons/wait-event-icon.png";
import ActionNodeIcon from "../../assets/icons/action-node-icon.png";
import NodeActionIcon from "../../assets/icons/node-action-icon.png";
import NodeConditionIcon from "../../assets/icons/node-condition-icon.png";
import NodeDelayIcon from "../../assets/icons/node-delay-icon.png";
import NodeTriggerIcon from "../../assets/icons/node-trigger-icon.png";
import WhatsAppDefaultWallpaper from "../../assets/images/whatsapp-default-wallpaper.png";
import Loader from "../../components/loader/Loader";
import "./automation-tree-editor.css";

const { Title, Text } = Typography;

type Props = {
    domain: string;
    automationId: number;
    editable?: boolean;
    showSettingsHeader?: boolean;
    showPublishInBoard?: boolean;
    openExitConditionSignal?: number;
    onFullscreenChange?: (isFullscreen: boolean) => void;
    stepsSource?: "live" | "published";
    highlightStepIndex?: number | null;
    stageHeight?: number | string;
    runHistoryByStep?: Record<number, { ranAt?: string; done?: boolean; doneAt?: string }>;
    runBranchSelectionByStep?: Record<number, { chosenNextStepIndexes?: number[]; transitionKey?: string }>;
    onDraftChanged?: () => void;
};

type StepType = "wait" | "condition" | "wait_event" | "action";
type ConditionGroupMode = "ALL_GROUPS" | "ANY_GROUP";
type ConditionRuleRow = {
    id: string;
    field: string;
    operator: string;
    value: string;
    count_threshold?: number | null;
};
type ConditionGroup = {
    id: string;
    rules: ConditionRuleRow[];
};

type NodeData = {
    id: number;
    x: number;
    y: number;
    level: number;
    row: number;
    width: number;
    height: number;
    step: V2AutomationStepDto;
};

type SuggestionNode = {
    id: string;
    x: number;
    y: number;
    edgeKey: string;
    title: string;
};

type EditableEdgeType = {
    id: string;
    fromStepIndex: number;
    toStepIndex: number;
    sortOrder: number;
    edgeKey: string;
    x: number;
    y: number;
    editable: boolean;
    allowedKeys: string[];
};
type InsertEdgeType = {
    id: string;
    fromStepIndex: number;
    toStepIndex: number;
    sortOrder: number;
    edgeKey: string;
    x: number;
    y: number;
};

type EditorHistorySnapshot = {
    name: string;
    description: string;
    active: boolean;
    steps: V2AutomationStepDto[];
    edges: V2AutomationEdgeDto[];
    nodeOffsets: Record<number, { x: number; y: number }>;
};

type EntryVisualGroup = {
    id: number;
    title: string;
    leaves: string[];
};

type EntryEditorLeaf = {
    itemId: number;
    basicId: number;
    eventName: string;
    aggregation: "exists" | "count";
    operator: string;
    threshold: number | null;
    entityScopeMode: "any" | "run_context" | "specific";
    entityType: string;
    entityId: string;
    timeScopeMode: "inherit" | "rolling_window" | "all_history";
    timeScopeSeconds: number | null;
};

type EntryEditorGroup = {
    conditionId: number;
    conditionItemId: number | null;
    title: string;
    timeframeSeconds: number | null;
    leaves: EntryEditorLeaf[];
};

const GRAPH_NODE_WIDTH = 190;
const GRAPH_NODE_HEIGHT = 74;
const GRAPH_X_GAP = 300;
const GRAPH_Y_GAP = 140;

const STEP_TYPE_OPTIONS = [
    { value: "wait", label: "Stand By", subtitle: "Time Delay" },
    { value: "condition", label: "Condition", subtitle: "true/false branch" },
    { value: "wait_event", label: "Wait Event", subtitle: "matched / timeout branch" },
    { value: "action", label: "Action", subtitle: "Action" },
];

function stepTypeIcon(type: string) {
    if (type === "wait") return <img src={StandByIcon} alt="" className="v2at-type-btn-icon-img" />;
    if (type === "condition") return <img src={ConditionIcon} alt="" className="v2at-type-btn-icon-img" />;
    if (type === "wait_event") return <img src={WaitEventIcon} alt="" className="v2at-type-btn-icon-img" />;
    return <img src={ActionNodeIcon} alt="" className="v2at-type-btn-icon-img" />;
}

const CONDITION_FIELD_OPTIONS = [
    { value: "event_name", label: "Event name" },
    { value: "event.entity_type", label: "Entity type" },
    { value: "event.entity_id", label: "Entity ID" },
    { value: "context.room_name", label: "Room name" },
    { value: "context.price", label: "Price" },
    { value: "member.amount_spent_usd", label: "Client total spent (USD)" },
    { value: "client.tags", label: "Client tags" },
];

const CONDITION_OPERATOR_OPTIONS = [
    { value: "equals", label: "equals" },
    { value: "not_equals", label: "not equals" },
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "not contains" },
    { value: "gt", label: ">" },
    { value: "gte", label: ">=" },
    { value: "lt", label: "<" },
    { value: "lte", label: "<=" },
    { value: "in", label: "in list" },
    { value: "count_gte", label: "count >= (matches)" },
    { value: "count_gt", label: "count > (matches)" },
    { value: "count_eq", label: "count = (matches)" },
    { value: "count_lte", label: "count <= (matches)" },
    { value: "count_lt", label: "count < (matches)" },
];

function makeConditionRuleRow(): ConditionRuleRow {
    return {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        field: "",
        operator: "equals",
        value: "",
        count_threshold: null,
    };
}

function makeConditionGroup(): ConditionGroup {
    return {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        rules: [makeConditionRuleRow()],
    };
}

function parseJsonMaybe(v: any) {
    if (v == null) return null;
    if (typeof v === "string") {
        try {
            return JSON.parse(v);
        } catch {
            return null;
        }
    }
    return v;
}

function parseTemplateVariablesSchema(raw: any): string[] {
    const parsed = parseJsonMaybe(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
        .map((x: any) => String(x || "").trim())
        .filter(Boolean);
}

function placeholderHelpText(key: string): string {
    if (key === "context.image_token") return "Image placeholder. Must come from tracked context for this run.";
    if (key === "context_action_token") return "CTA/action placeholder. Must come from tracked context for this run.";
    if (key.startsWith("context.")) return "Mapped from activity meta.context.*";
    if (key.startsWith("client.")) return "Mapped from client profile fields.";
    if (key.startsWith("member.")) return "Mapped from shop-client relation fields.";
    return "Mapped from runtime context.";
}

function displayPlaceholderKey(key: string): string {
    if (key === "context.image_token") return "context.image_url";
    if (key === "context.action_token") return "context.callback_url";
    return key;
}

function placeholderExampleValue(key: string): string {
    if (key === "context.image_token") return "room_123_main_image";
    if (key === "context_action_token") return "open_room_123";
    if (key === "context.room_name") return "Deluxe Suite";
    if (key === "context.price") return "149.99";
    if (key === "context.callback_url") return "https://example.com/rooms/123";
    if (key === "context.image_url") return "https://example.com/images/room-123.jpg";
    if (key.includes("context.images_urls.")) return "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85";
    if (key.includes("context.buttons.") && key.endsWith(".url")) return "https://vuelve.co";
    if (key === "client.first_name") return "John";
    if (key === "client.last_name") return "Doe";
    if (key === "client.full_name") return "John Doe";
    if (key === "client.email") return "john@example.com";
    if (key === "client.phone") return "+14155550123";
    if (key === "member.amount_spent_usd") return "1240.50";
    if (key.startsWith("context.")) return `"${key.replace("context.", "")}_value"`;
    if (key.startsWith("client.")) return `"${key.replace("client.", "")}_value"`;
    if (key.startsWith("member.")) return `"${key.replace("member.", "")}_value"`;
    return "example_value";
}

function parseEntryLeafScopes(filtersJson: any) {
    const parsed = parseJsonMaybe(filtersJson);
    const scope = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed.scope : null;

    const entityModeRaw = String(scope?.entity?.mode || "").toLowerCase();
    const entityScopeMode: "any" | "run_context" | "specific" =
        entityModeRaw === "run_context" ? "run_context" : entityModeRaw === "specific" ? "specific" : "any";
    const entityType = String(scope?.entity?.entity_type ?? scope?.entity?.type ?? "");
    const entityId = String(scope?.entity?.entity_id ?? scope?.entity?.id ?? "");

    const timeModeRaw = String(scope?.time?.mode || "").toLowerCase();
    const timeScopeMode: "inherit" | "rolling_window" | "all_history" =
        timeModeRaw === "rolling_window" ? "rolling_window" : timeModeRaw === "all_history" ? "all_history" : "inherit";
    const timeScopeSeconds = Number(scope?.time?.seconds || 0);

    return {
        entityScopeMode,
        entityType,
        entityId,
        timeScopeMode,
        timeScopeSeconds: Number.isFinite(timeScopeSeconds) && timeScopeSeconds > 0 ? timeScopeSeconds : null,
    };
}

function buildEntryLeafFiltersJson(input: {
    entityScopeMode: "any" | "run_context" | "specific";
    entityType?: string;
    entityId?: string;
    timeScopeMode: "inherit" | "rolling_window" | "all_history";
    timeScopeSeconds?: number | null;
}) {
    const out: any = { scope: { entity: {}, time: {} } };

    out.scope.entity.mode = input.entityScopeMode;
    if (input.entityScopeMode === "specific") {
        out.scope.entity.entity_type = String(input.entityType || "").trim();
        out.scope.entity.entity_id = String(input.entityId || "").trim();
    }

    out.scope.time.mode = input.timeScopeMode;
    if (input.timeScopeMode === "rolling_window") {
        const seconds = Number(input.timeScopeSeconds || 0);
        out.scope.time.seconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 3600;
    }

    return out;
}

function isSelectableWhatsApp(t: V2TemplateListItemDto) {
    const status = String(t.status || "").toLowerCase();
    const isReady = status === "ready";
    const contentTypes = parseJsonMaybe(t.content_types_json);
    const types = Array.isArray(contentTypes) ? contentTypes : [];
    const isMenu = types.includes("twilio/list-picker");
    const hasContent = !!t.provider_content_sid;
    return t.channel === "whatsapp" && (status === "approved" || isReady || (isMenu && hasContent));
}

function normalizeIncomingStep(s: V2AutomationStepDto): V2AutomationStepDto {
    if (s.step_type === "send_whatsapp_template") {
        return {
            step_index: s.step_index,
            step_type: "action",
            config: { action_type: "send_whatsapp_template", ...(s.config || {}) },
        };
    }
    return s;
}

function stepLabel(step: V2AutomationStepDto) {
    if (step.step_type === "wait") return "Stand By";
    if (step.step_type === "condition") return "Condition";
    if (step.step_type === "wait_event") return "Wait Event";
    if (step.step_type === "action") {
        const actionType = String(step.config?.action_type || "send_whatsapp_template");
        if (actionType === "send_whatsapp_template") return "Action: Send WhatsApp";
        if (actionType === "webhook") return "Action: Webhook";
        if (actionType === "tag_client") return "Action: Tag Client";
        if (actionType === "set_property") return "Action: Set Property";
        return "Action";
    }
    return String(step.step_type || "Step");
}

function stepTypeClass(step: V2AutomationStepDto): string {
    if (step.step_type === "wait") return "type-wait";
    if (step.step_type === "wait_event") return "type-wait-event";
    if (step.step_type === "condition") return "type-condition";
    if (step.step_type === "action") {
        const actionType = String(step.config?.action_type || "send_whatsapp_template");
        if (actionType === "send_whatsapp_template") return "type-action-send";
        return "type-action";
    }
    return "type-default";
}

function formatFriendlyDuration(totalSecondsRaw: any): string {
    const totalSeconds = Math.max(0, Number(totalSecondsRaw || 0));
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return "no timeout";
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts: string[] = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds || !parts.length) parts.push(`${seconds}s`);
    return parts.join(" ");
}

function conditionOperatorLabel(op: string): string {
    const map: Record<string, string> = {
        equals: "equals",
        not_equals: "not equals",
        contains: "contains",
        not_contains: "not contains",
        gt: "greater than",
        gte: "greater or equal",
        lt: "less than",
        lte: "less or equal",
        in: "in list",
    };
    return map[String(op || "").trim()] || String(op || "equals");
}

function conditionSummaryText(config: any): string {
    const tree = config?.condition_tree_json || config?.condition_tree;
    if (tree && typeof tree === "object" && Array.isArray(tree.groups)) {
        const mode = String(tree.mode || "ALL_GROUPS");
        const betweenGroups = mode === "ANY_GROUP" ? " OR " : " AND ";
        const groupTexts = tree.groups
            .map((g: any) => {
                const rules = Array.isArray(g?.rules) ? g.rules : [];
                const ruleText = rules
                    .map((r: any) => `${String(r?.field || "?")} ${conditionOperatorLabel(String(r?.operator || "equals"))} ${String(r?.value || "?")}`)
                    .join(" AND ");
                return ruleText ? `(${ruleText})` : "";
            })
            .filter(Boolean);
        return groupTexts.join(betweenGroups);
    }
    if (Array.isArray(tree)) {
        const ruleText = tree
            .map((r: any) => `${String(r?.field || "?")} ${conditionOperatorLabel(String(r?.operator || "equals"))} ${String(r?.value || "?")}`)
            .filter((x: string) => x.trim().length > 0)
            .join(" AND ");
        return ruleText || "Open node to set condition";
    }
    return "Open node to set condition";
}

function buildDefaultStep(stepIndex: number, type: StepType = "action"): V2AutomationStepDto {
    if (type === "wait") return { step_index: stepIndex, step_type: "wait", config: { delay_seconds: 60 } };
    if (type === "condition") return { step_index: stepIndex, step_type: "condition", config: { condition_id: null } };
    if (type === "wait_event") {
        return {
            step_index: stepIndex,
            step_type: "wait_event",
            config: { event_name: "", poll_seconds: 30, timeout_seconds: 3600 },
        };
    }
    return {
        step_index: stepIndex,
        step_type: "action",
        config: { action_type: "send_whatsapp_template", template_id: null, variables_mapping: {} },
    };
}

function defaultLinearEdges(steps: V2AutomationStepDto[]): V2AutomationEdgeDto[] {
    const sorted = [...steps].sort((a, b) => Number(a.step_index) - Number(b.step_index));
    const out: V2AutomationEdgeDto[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
        out.push({
            from_step_index: Number(sorted[i].step_index),
            to_step_index: Number(sorted[i + 1].step_index),
            edge_key: "default",
            sort_order: i,
        });
    }
    return out;
}

function firstWaitEventName(steps: V2AutomationStepDto[]): string {
    const sorted = [...steps].sort((a, b) => Number(a.step_index) - Number(b.step_index));
    const waitEvent = sorted.find((s) => String(s.step_type) === "wait_event");
    return String(waitEvent?.config?.event_name || "").trim();
}

function findStepZeroWaitEvent(steps: V2AutomationStepDto[]): V2AutomationStepDto | null {
    return steps.find((s) => Number(s.step_index) === 0 && String(s.step_type) === "wait_event") || null;
}

function runtimeValidationErrors(steps: V2AutomationStepDto[], edges: V2AutomationEdgeDto[]): string[] {
    const errors: string[] = [];
    const byId = new Map<number, V2AutomationStepDto>();
    for (const s of steps) byId.set(Number(s.step_index), s);

    if (!steps.length) return ["Add at least one node."];
    if (!byId.has(0)) errors.push("Step 0 is required.");

    for (const s of steps) {
        const idx = Number(s.step_index);
        const type = String(s.step_type);
        const cfg = s.config || {};
        if (type === "wait") {
            if (!Number.isFinite(Number(cfg.delay_seconds)) || Number(cfg.delay_seconds) <= 0) {
                errors.push(`Step ${idx}: Stand By delay must be greater than 0.`);
            }
        } else if (type === "wait_event") {
            const eventName = String(cfg.event_name || "").trim();
            if (!eventName) errors.push(`Step ${idx}: Wait Event requires event name.`);
            if (!Number.isFinite(Number(cfg.poll_seconds)) || Number(cfg.poll_seconds) < 5) {
                errors.push(`Step ${idx}: Wait Event poll must be at least 5 seconds.`);
            }
            if (!Number.isFinite(Number(cfg.timeout_seconds)) || Number(cfg.timeout_seconds) <= 0) {
                errors.push(`Step ${idx}: Wait Event timeout must be greater than 0.`);
            }
            const linkageMode = String(cfg.entity_linkage_mode || "any");
            if (!["any", "run_context", "specific"].includes(linkageMode)) {
                errors.push(`Step ${idx}: Wait Event has invalid entity scope.`);
            }
            if (linkageMode === "specific") {
                if (!String(cfg.entity_type || "").trim() || !String(cfg.entity_id ?? "").trim()) {
                    errors.push(`Step ${idx}: Wait Event specific entity requires type + id.`);
                }
            }
        } else if (type === "condition") {
            const tree = cfg.condition_tree_json || cfg.condition_tree;
            const groups = Array.isArray(tree?.groups) ? tree.groups : [];
            const validGroups = groups.filter((g: any) => Array.isArray(g?.rules) && g.rules.length > 0);
            if (!validGroups.length) {
                errors.push(`Step ${idx}: Condition requires at least one rule.`);
            }
            const scopeMode = String(cfg.condition_scope_mode || "since_run_start");
            if (scopeMode === "rolling_window") {
                const win = Number(cfg.condition_window_seconds || 0);
                if (!Number.isFinite(win) || win <= 0) {
                    errors.push(`Step ${idx}: Condition rolling window must be greater than 0 seconds.`);
                }
            }
            const linkageMode = String(cfg.entity_linkage_mode || "any");
            if (!["any", "run_context", "specific"].includes(linkageMode)) {
                errors.push(`Step ${idx}: Condition has invalid entity scope.`);
            }
            if (linkageMode === "specific") {
                if (!String(cfg.entity_type || "").trim() || !String(cfg.entity_id ?? "").trim()) {
                    errors.push(`Step ${idx}: Condition specific entity requires type + id.`);
                }
            }
        } else if (type === "action") {
            const actionType = String(cfg.action_type || "send_whatsapp_template");
            if (actionType === "send_whatsapp_template" && !Number(cfg.template_id || 0)) {
                errors.push(`Step ${idx}: Select a WhatsApp template.`);
            } else if (actionType === "send_whatsapp_template") {
                const missingPolicy = String(cfg.missing_variable_policy || "skip_step");
                if (!["skip_step", "fail_run"].includes(missingPolicy)) {
                    errors.push(`Step ${idx}: Missing variable policy is invalid.`);
                }
            } else if (actionType === "tag_client") {
                if (!String(cfg.tag || "").trim()) errors.push(`Step ${idx}: Tag is required.`);
                const op = String(cfg.op || "add").toLowerCase();
                if (op !== "add" && op !== "remove") errors.push(`Step ${idx}: Tag action must be add/remove.`);
            } else if (actionType === "set_property") {
                if (!String(cfg.property_key || "").trim()) errors.push(`Step ${idx}: Property key is required.`);
                const hasValue =
                    cfg.value !== undefined ||
                    cfg.value_json !== undefined ||
                    cfg.value_text !== undefined;
                if (!hasValue) errors.push(`Step ${idx}: Property value is required.`);
            } else if (actionType === "webhook") {
                if (!/^https?:\/\//i.test(String(cfg.webhook_url || "").trim())) {
                    errors.push(`Step ${idx}: Valid webhook URL is required.`);
                }
            }
        }
    }

    const outgoing = new Map<number, V2AutomationEdgeDto[]>();
    for (const e of edges) {
        const from = Number(e.from_step_index);
        const to = Number(e.to_step_index);
        const key = String(e.edge_key || "").trim();
        if (!byId.has(from)) errors.push(`Edge source step ${from} is missing.`);
        if (!byId.has(to)) errors.push(`Edge target step ${to} is missing.`);
        if (!key) errors.push(`Edge ${from} -> ${to} has empty path key.`);
        if (!outgoing.has(from)) outgoing.set(from, []);
        outgoing.get(from)!.push(e);
    }

    const nextById = new Map<number, number[]>();
    for (const e of edges) {
        const from = Number(e.from_step_index);
        const to = Number(e.to_step_index);
        if (!nextById.has(from)) nextById.set(from, []);
        nextById.get(from)!.push(to);
    }
    const visit = new Set<number>();
    const inStack = new Set<number>();
    let hasCycle = false;
    const dfs = (id: number) => {
        if (hasCycle) return;
        visit.add(id);
        inStack.add(id);
        for (const to of nextById.get(id) || []) {
            if (!visit.has(to)) dfs(to);
            else if (inStack.has(to)) hasCycle = true;
            if (hasCycle) break;
        }
        inStack.delete(id);
    };
    if (byId.has(0)) dfs(0);
    if (hasCycle) errors.push("Flow contains a cycle. Use explicit waits/conditions without loops.");

    for (const s of steps) {
        const idx = Number(s.step_index);
        const out = outgoing.get(idx) || [];
        const type = String(s.step_type);
        const keys = new Set(out.map((e) => String(e.edge_key || "").trim()));

        if (type === "condition") {
            const invalid = out.find((e) => !["true", "false"].includes(String(e.edge_key || "").trim()));
            if (invalid) errors.push(`Step ${idx}: Condition edges must be true/false.`);
        } else if (type === "wait_event") {
            const invalid = out.find((e) => !["matched", "timeout"].includes(String(e.edge_key || "").trim()));
            if (invalid) errors.push(`Step ${idx}: Wait Event edges must be matched/timeout.`);
        } else {
            const invalid = out.find((e) => String(e.edge_key || "default") !== "default");
            if (invalid) {
                errors.push(`Step ${idx}: non-branch steps can only use 'default' path.`);
            }
        }
    }

    return Array.from(new Set(errors));
}

function allowedEdgeKeysForStepType(stepType: string): string[] {
    const t = String(stepType || "");
    if (t === "wait_event") return ["matched", "timeout"];
    if (t === "condition") return ["true", "false"];
    return ["default"];
}

function normalizeEdgeKeyForStepType(stepType: string, key: string): string {
    const t = String(stepType || "");
    const k = String(key || "").trim();
    if (t === "wait_event") return ["matched", "timeout"].includes(k) ? k : "matched";
    if (t === "condition") return ["true", "false"].includes(k) ? k : "true";
    return "default";
}

function remapEdgeKeyByTypeChange(fromType: string, toType: string, key: string): string {
    const from = String(fromType || "");
    const to = String(toType || "");
    const k = String(key || "").trim();

    if (from === "wait_event" && to === "condition") {
        if (k === "matched") return "true";
        if (k === "timeout") return "false";
    }
    if (from === "condition" && to === "wait_event") {
        if (k === "true") return "matched";
        if (k === "false") return "timeout";
    }

    return normalizeEdgeKeyForStepType(to, k);
}

function entryLeafText(basic: any): string {
    const eventName = String(basic?.event_name || "").trim() || "event";
    const agg = String(basic?.aggregation || "exists");
    const op = String(basic?.operator || (agg === "count" ? ">=" : "EXISTS"));
    const threshold = basic?.threshold;
    if (agg === "count") {
        const th = Number(threshold);
        return `${eventName} count ${op} ${Number.isFinite(th) ? th : 1}`;
    }
    return `${eventName} ${op}`;
}

function buildEntryVisual(bundle: TreeBundle | null): { mode: "AND" | "OR"; groups: EntryVisualGroup[] } {
    if (!bundle) return { mode: "AND", groups: [] };
    const rootId = Number(bundle.root_condition_id || 0);
    const root = bundle.conditionsById?.[String(rootId)] || null;
    const rootItems = Array.isArray(bundle.itemsByConditionId?.[String(rootId)])
        ? bundle.itemsByConditionId[String(rootId)]
        : [];
    const mode = String(root?.operator || "AND").toUpperCase() === "OR" ? "OR" : "AND";

    const groups: EntryVisualGroup[] = [];
    const rootBasicItems = rootItems.filter((i: any) => String(i?.item_type) === "basic_condition");
    const childGroups = rootItems.filter((i: any) => String(i?.item_type) === "condition");
    if (childGroups.length) {
        if (rootBasicItems.length) {
            const rootLeaves = rootBasicItems
                .map((it: any) => bundle.basicById?.[String(Number(it?.item_id || 0))])
                .filter(Boolean)
                .map(entryLeafText);
            groups.push({
                id: rootId,
                title: String(root?.title || "Group 1"),
                leaves: rootLeaves.length ? rootLeaves : ["No conditions yet"],
            });
        }
        for (const g of childGroups) {
            const gid = Number(g.item_id || 0);
            const gc = bundle.conditionsById?.[String(gid)] || null;
            const gItems = Array.isArray(bundle.itemsByConditionId?.[String(gid)])
                ? bundle.itemsByConditionId[String(gid)]
                : [];
            const leaves = gItems
                .filter((it: any) => String(it?.item_type) === "basic_condition")
                .map((it: any) => bundle.basicById?.[String(Number(it?.item_id || 0))])
                .filter(Boolean)
                .map(entryLeafText);
            groups.push({
                id: gid,
                title: String(gc?.title || `Group ${groups.length + 1}`),
                leaves: leaves.length ? leaves : ["No conditions yet"],
            });
        }
    } else {
        const leaves = rootItems
            .filter((it: any) => String(it?.item_type) === "basic_condition")
            .map((it: any) => bundle.basicById?.[String(Number(it?.item_id || 0))])
            .filter(Boolean)
            .map(entryLeafText);
        groups.push({
            id: rootId,
            title: "Group 1",
            leaves: leaves.length ? leaves : ["No conditions yet"],
        });
    }

    return { mode, groups };
}

export default function AutomationTreeEditor({
    domain,
    automationId,
    editable = true,
    showSettingsHeader,
    showPublishInBoard,
    openExitConditionSignal,
    onFullscreenChange,
    stepsSource = "live",
    highlightStepIndex = null,
    stageHeight,
    runHistoryByStep = {},
    runBranchSelectionByStep = {},
    onDraftChanged,
}: Props) {
    const { showToast, closeToast } = useToast();
    const showHeaderSection = showSettingsHeader ?? editable;
    const showPublishButton = showPublishInBoard ?? editable;
    const compactCanvasShell = editable && !showHeaderSection;
    const { isFlagOn } = useFlag();
    const showStepNumber = isFlagOn("automation.show_step_number");
    const [loading, setLoading] = useState(true);
    const [loadingTrigger, setLoadingTrigger] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveValidationErrors, setSaveValidationErrors] = useState<string[]>([]);
    const [undoStack, setUndoStack] = useState<EditorHistorySnapshot[]>([]);
    const [redoStack, setRedoStack] = useState<EditorHistorySnapshot[]>([]);
    const [validationDetailsOpen, setValidationDetailsOpen] = useState(false);
    const [edgeEditOpen, setEdgeEditOpen] = useState(false);
    const [edgeEditTarget, setEdgeEditTarget] = useState<EditableEdgeType | null>(null);
    const [edgeEditKey, setEdgeEditKey] = useState<string>("default");
    const [hoveredInsertEdgeId, setHoveredInsertEdgeId] = useState<string | null>(null);
    const [hoveredSuggestionId, setHoveredSuggestionId] = useState<string | null>(null);
    const [hoveredEntryGroupId, setHoveredEntryGroupId] = useState<number | null>(null);
    const [name, setName] = useState("");
    const [description, setDescription] = useState<string>("");
    const [active, setActive] = useState(false);
    const [steps, setSteps] = useState<V2AutomationStepDto[]>([]);
    const [edges, setEdges] = useState<V2AutomationEdgeDto[]>([]);
    const [primaryTriggerId, setPrimaryTriggerId] = useState<number | null>(null);
    const [primaryTriggerEventName, setPrimaryTriggerEventName] = useState<string>("");
    const [primaryTriggerTitle, setPrimaryTriggerTitle] = useState<string>("");
    const [primaryTriggerCooldownSeconds, setPrimaryTriggerCooldownSeconds] = useState<number>(0);
    const [primaryTriggerMaxActiveRuns, setPrimaryTriggerMaxActiveRuns] = useState<number>(1);
    const [primaryTriggerActive, setPrimaryTriggerActive] = useState<boolean>(true);
    const [entryConditionId, setEntryConditionId] = useState<number | null>(null);
    const [exitConditionId, setExitConditionId] = useState<number | null>(null);
    const [exitConditionModalOpen, setExitConditionModalOpen] = useState(false);
    const [entryTreeBundle, setEntryTreeBundle] = useState<TreeBundle | null>(null);
    const [entryGroupModalOpen, setEntryGroupModalOpen] = useState(false);
    const [editingEntryGroupId, setEditingEntryGroupId] = useState<number | null>(null);
    const [templates, setTemplates] = useState<V2TemplateListItemDto[]>([]);
    const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalForm] = Form.useForm();
    const modalStepType = (Form.useWatch("step_type", modalForm) || "action") as StepType;
    const [zoom, setZoom] = useState<number>(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [hoveredNodeId, setHoveredNodeId] = useState<number | null>(null);
    const [openNodeMenuId, setOpenNodeMenuId] = useState<number | null>(null);
    const [conditionGroupMode, setConditionGroupMode] = useState<ConditionGroupMode>("ALL_GROUPS");
    const [conditionGroups, setConditionGroups] = useState<ConditionGroup[]>([makeConditionGroup()]);
    const [nodeOffsets, setNodeOffsets] = useState<Record<number, { x: number; y: number }>>({});
    const [draggingStepId, setDraggingStepId] = useState<number | null>(null);
    const [suppressClickForStepId, setSuppressClickForStepId] = useState<number | null>(null);
    const autosaveTimerRef = React.useRef<number | null>(null);
    const lastSavedSnapshotRef = React.useRef<string>("");
    const autosaveGenerationRef = React.useRef(0);
    const forceImmediateSaveRef = React.useRef(false);
    const historyApplyingRef = React.useRef(false);
    const historyInitializedRef = React.useRef(false);
    const lastHistorySnapshotRef = React.useRef<string>("");
    const dragRef = React.useRef<null | {
        stepId: number;
        startX: number;
        startY: number;
        originX: number;
        originY: number;
        moved: boolean;
        wasSelectedBefore: boolean;
    }>(null);
    const offsetsRef = React.useRef<Record<number, { x: number; y: number }>>({});
    const boardCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const nodeIconImagesRef = React.useRef<Record<string, HTMLImageElement>>({});
    const mediaPreviewImagesRef = React.useRef<Record<string, HTMLImageElement>>({});
    const [iconRenderVersion, setIconRenderVersion] = useState(0);

    useEffect(() => {
        offsetsRef.current = nodeOffsets;
    }, [nodeOffsets]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            lastSavedSnapshotRef.current = "";
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
            historyInitializedRef.current = false;
            lastHistorySnapshotRef.current = "";
            setUndoStack([]);
            setRedoStack([]);
            setLoading(true);
            setLoadingTrigger(true);
            const [automationRes, graphRes, templatesRes, triggersRes] = await Promise.all([
                GetV2Automation(domain, automationId),
                GetV2AutomationSteps(domain, automationId, stepsSource),
                GetV2TemplatesContextList(domain),
                GetV2AutomationTriggers(domain, automationId),
            ]);

            if (cancelled) return;

            if (!automationRes.ok) {
                message.error(automationRes.error || "Failed loading automation");
                setLoading(false);
                setLoadingTrigger(false);
                return;
            }
            setName(String(automationRes.data?.name || ""));
            setDescription(String(automationRes.data?.description || ""));
            setActive(Boolean(automationRes.data?.active));

            let loadedSteps: V2AutomationStepDto[] = [];
            if (!graphRes.ok) {
                message.error(graphRes.error || "Failed loading graph");
                setSteps([]);
                setEdges([]);
                setNodeOffsets({});
            } else {
                loadedSteps = (graphRes.data?.steps || [])
                    .map(normalizeIncomingStep)
                    .sort(
                        (a: V2AutomationStepDto, b: V2AutomationStepDto) =>
                            Number(a.step_index) - Number(b.step_index)
                    );
                const loadedEdges = (graphRes.data?.edges || []).slice();
                setSteps(loadedSteps);
                setEdges(loadedEdges.length ? loadedEdges : defaultLinearEdges(loadedSteps));
                const persistedOffsets: Record<number, { x: number; y: number }> = {};
                for (const s of loadedSteps) {
                    const ui = (s.config && (s.config.ui_position || s.config.uiPosition)) || null;
                    const x = Number(ui?.x);
                    const y = Number(ui?.y);
                    if (Number.isFinite(x) || Number.isFinite(y)) {
                        persistedOffsets[Number(s.step_index)] = {
                            x: Number.isFinite(x) ? x : 0,
                            y: Number.isFinite(y) ? y : 0,
                        };
                    }
                }
                setNodeOffsets(persistedOffsets);
            }

            let triggerId: number | null = null;
            let triggerEvent = "";
            let triggerTitle = "";
            let triggerCooldown = 0;
            let triggerMaxActive = 1;
            let triggerActive = true;
            let loadedEntryConditionId: number | null = null;
            let loadedExitConditionId: number | null = null;
            if (!triggersRes.ok) {
                message.error(triggersRes.error || "Failed loading triggers");
            } else {
                const triggerList = Array.isArray(triggersRes.data) ? triggersRes.data : [];
                const picked = triggerList[0];
                if (picked) {
                    triggerId = Number(picked.id || 0) || null;
                    triggerTitle = String(picked.title || "");
                    triggerEvent = String(picked.entry_event_name || "").trim();
                    triggerCooldown = Math.max(0, Number((picked as any).cooldown_seconds || 0));
                    triggerMaxActive = Math.max(1, Number((picked as any).max_active_runs_per_client || 1));
                    triggerActive = Number((picked as any).active || 0) === 1;
                    loadedEntryConditionId = Number(picked.entry_condition_id || 0) || null;
                    loadedExitConditionId = Number(picked.exit_condition_id || 0) || null;
                }
            }

            if (!triggerId) {
                const created = await CreateV2AutomationTrigger(domain, automationId, {
                    title: `${String(automationRes.data?.name || "").trim() || "Automation"} Trigger`,
                    event_name: firstWaitEventName(loadedSteps) || "event.name",
                    active: 1,
                    cooldown_seconds: 0,
                    max_active_runs_per_client: 1,
                });
                if (!created.ok) {
                    message.error(created.error || "Failed creating automation trigger");
                } else {
                    triggerId = Number(created.data?.id || created.data?.triggerId || 0) || null;
                    triggerTitle = `${String(automationRes.data?.name || "").trim() || "Automation"} Trigger`;
                    triggerEvent = String(created.data?.entry_event_name || "").trim();
                    triggerCooldown = 0;
                    triggerMaxActive = 1;
                    triggerActive = true;
                    loadedEntryConditionId = Number(created.data?.entry_condition_id || 0) || null;
                    loadedExitConditionId = null;
                }
            }

            setPrimaryTriggerId(triggerId);
            setPrimaryTriggerTitle(triggerTitle);
            setPrimaryTriggerEventName(triggerEvent);
            setPrimaryTriggerCooldownSeconds(triggerCooldown);
            setPrimaryTriggerMaxActiveRuns(triggerMaxActive);
            setPrimaryTriggerActive(triggerActive);
            setEntryConditionId(loadedEntryConditionId);
            setExitConditionId(loadedExitConditionId);
            setLoadingTrigger(false);

            if (triggerEvent && loadedSteps.length) {
                const stepZeroWait = findStepZeroWaitEvent(loadedSteps);
                if (stepZeroWait && String(stepZeroWait.config?.event_name || "").trim() !== triggerEvent) {
                    const patched = loadedSteps.map((s) =>
                        Number(s.step_index) === 0
                            ? { ...s, config: { ...(s.config || {}), event_name: triggerEvent } }
                            : s
                    );
                    setSteps(patched);
                }
            }

            if (!templatesRes.ok) {
                message.error(templatesRes.error || "Failed loading templates");
                setTemplates([]);
            } else {
                setTemplates((templatesRes.data?.templates || []).filter(isSelectableWhatsApp));
            }

            setLoading(false);
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [domain, automationId, stepsSource]);

    useEffect(() => {
        if (!isFullscreen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [isFullscreen]);

    useEffect(() => {
        let cancelled = false;
        const loadEntryTree = async () => {
            if (!entryConditionId) {
                setEntryTreeBundle(null);
                return;
            }
            const res = await GetConditionTreeBundle(domain, entryConditionId);
            if (cancelled) return;
            if (!res.ok) {
                setEntryTreeBundle(null);
                return;
            }
            setEntryTreeBundle(res.data || null);
        };
        loadEntryTree();
        return () => {
            cancelled = true;
        };
    }, [domain, entryConditionId]);

    const entryEditor = useMemo(() => {
        if (!entryTreeBundle) return null;
        const rootId = Number(entryTreeBundle.root_condition_id || 0);
        const root = entryTreeBundle.conditionsById?.[String(rootId)] || null;
        const rootItems = Array.isArray(entryTreeBundle.itemsByConditionId?.[String(rootId)])
            ? entryTreeBundle.itemsByConditionId[String(rootId)]
            : [];
        const groups: EntryEditorGroup[] = [];
        const conditionItems = rootItems.filter((it: any) => String(it?.item_type) === "condition");
        const rootBasicItems = rootItems.filter((it: any) => String(it?.item_type) === "basic_condition");
        const mapLeaves = (conditionId: number): EntryEditorLeaf[] => {
            const items = Array.isArray(entryTreeBundle.itemsByConditionId?.[String(conditionId)])
                ? entryTreeBundle.itemsByConditionId[String(conditionId)]
                : [];
            return items
                .filter((it: any) => String(it?.item_type) === "basic_condition")
                .map((it: any) => {
                    const basicId = Number(it?.item_id || 0);
                    const basic = entryTreeBundle.basicById?.[String(basicId)] || {};
                    const scopes = parseEntryLeafScopes(basic?.filters_json);
                    return {
                        itemId: Number(it?.id || 0),
                        basicId,
                        eventName: String(basic?.event_name || ""),
                        aggregation: String(basic?.aggregation || "exists") === "count" ? "count" : "exists",
                        operator: String(basic?.operator || "EXISTS"),
                        threshold:
                            basic?.threshold == null || basic?.threshold === ""
                                ? null
                                : Number(basic.threshold),
                        entityScopeMode: scopes.entityScopeMode,
                        entityType: scopes.entityType,
                        entityId: scopes.entityId,
                        timeScopeMode: scopes.timeScopeMode,
                        timeScopeSeconds: scopes.timeScopeSeconds,
                    };
                });
        };

        if (conditionItems.length) {
            // Keep root-level basic conditions visible as their own editable group.
            // Without this, adding the first child group makes the existing root group seem to disappear.
            if (rootBasicItems.length) {
                groups.push({
                    conditionId: rootId,
                    conditionItemId: null,
                    title: String(root?.title || "Group"),
                    timeframeSeconds:
                        root?.timeframe_seconds == null || root?.timeframe_seconds === ""
                            ? null
                            : Number(root.timeframe_seconds),
                    leaves: mapLeaves(rootId),
                });
            }
            for (const ci of conditionItems) {
                const conditionId = Number(ci?.item_id || 0);
                const c = entryTreeBundle.conditionsById?.[String(conditionId)] || {};
                groups.push({
                    conditionId,
                    conditionItemId: Number(ci?.id || 0) || null,
                    title: String(c?.title || "Group"),
                    timeframeSeconds:
                        c?.timeframe_seconds == null || c?.timeframe_seconds === ""
                            ? null
                            : Number(c.timeframe_seconds),
                    leaves: mapLeaves(conditionId),
                });
            }
        } else {
            groups.push({
                conditionId: rootId,
                conditionItemId: null,
                title: "Group 1",
                timeframeSeconds:
                    root?.timeframe_seconds == null || root?.timeframe_seconds === ""
                        ? null
                        : Number(root.timeframe_seconds),
                leaves: mapLeaves(rootId),
            });
        }

        return {
            rootConditionId: rootId,
            rootOperator: String(root?.operator || "AND").toUpperCase() === "OR" ? "OR" : "AND",
            groups,
        };
    }, [entryTreeBundle]);

    const editingEntryGroup = useMemo(
        () => (entryEditor?.groups || []).find((g) => Number(g.conditionId) === Number(editingEntryGroupId)) || null,
        [entryEditor, editingEntryGroupId]
    );
    const hasAnyEntryGroups =
        (entryEditor?.groups || []).filter(
            (g) => Number(g?.conditionItemId || 0) > 0 || (Array.isArray(g?.leaves) && g.leaves.length > 0)
        ).length > 0;
    const hasMultipleEntryGroups = hasAnyEntryGroups && (entryEditor?.groups?.length || 0) > 1;

    const stepIds = useMemo(() => steps.map((s) => Number(s.step_index)), [steps]);

    const graph = useMemo(() => {
        const templateById = new Map<number, V2TemplateListItemDto>(
            (templates || []).map((t) => [Number(t.id), t as V2TemplateListItemDto])
        );
        const estimateStepSize = (step: V2AutomationStepDto): { width: number; height: number } => {
            const type = String(step?.step_type || "");
            if (type !== "action") return { width: GRAPH_NODE_WIDTH, height: GRAPH_NODE_HEIGHT };
            const actionType = String(step?.config?.action_type || "");
            if (actionType !== "send_whatsapp_template") return { width: GRAPH_NODE_WIDTH, height: GRAPH_NODE_HEIGHT };
            const templateId = Number(step?.config?.template_id || 0);
            const template = templateById.get(templateId);
            const typesCfg = parseJsonMaybe((template as any)?.types_config_json) || {};
            const card = (typesCfg && (typesCfg["twilio/card"] || typesCfg["whatsapp/card"])) || null;
            const textCfg = (typesCfg && (typesCfg["twilio/text"] || typesCfg["whatsapp/text"])) || null;
            const title = String(
                card?.title_text || card?.title || textCfg?.title_text || textCfg?.title || (template as any)?.title || ""
            );
            const body = String(card?.body_text || card?.body || textCfg?.body_text || textCfg?.body || "");
            const hasMedia = Boolean(
                card?.image_url ||
                card?.image_var_path ||
                (Array.isArray(card?.media) && card.media.length)
            );
            const totalLen = `${title} ${body}`.trim().length;
            const lineEstimate = Math.max(2, Math.min(8, Math.ceil(totalLen / 42)));
            const base = 84;
            const mediaPart = hasMedia ? 132 : 0;
            return {
                width: 260,
                height: Math.max(124, Math.min(320, base + mediaPart + lineEstimate * 14)),
            };
        };

        const incoming = new Map<number, number>();
        const outgoing = new Map<number, number>();
        for (const id of stepIds) {
            incoming.set(id, 0);
            outgoing.set(id, 0);
        }
        for (const e of edges) {
            const from = Number(e.from_step_index);
            const to = Number(e.to_step_index);
            if (!incoming.has(to) || !outgoing.has(from)) continue;
            incoming.set(to, (incoming.get(to) || 0) + 1);
            outgoing.set(from, (outgoing.get(from) || 0) + 1);
        }

        let roots = stepIds.filter((id) => (incoming.get(id) || 0) === 0);
        if (!roots.length && stepIds.length) roots = [Math.min(...stepIds)];

        const levelByStep = new Map<number, number>();
        const q = [...roots];
        for (const r of roots) levelByStep.set(r, 0);
        while (q.length) {
            const cur = q.shift()!;
            const curLevel = levelByStep.get(cur) || 0;
            const outs = edges.filter((e) => Number(e.from_step_index) === cur);
            for (const out of outs) {
                const to = Number(out.to_step_index);
                const nextLevel = curLevel + 1;
                const prev = levelByStep.get(to);
                // Keep first discovered level to avoid infinite promotion loops on cyclic edges.
                if (prev == null) {
                    levelByStep.set(to, nextLevel);
                    q.push(to);
                }
            }
        }
        for (const id of stepIds) if (!levelByStep.has(id)) levelByStep.set(id, 0);

        const byLevel = new Map<number, number[]>();
        for (const id of stepIds) {
            const lv = levelByStep.get(id) || 0;
            if (!byLevel.has(lv)) byLevel.set(lv, []);
            byLevel.get(lv)!.push(id);
        }
        Array.from(byLevel.values()).forEach((list) => list.sort((a, b) => a - b));

        const nodeWidth = GRAPH_NODE_WIDTH;
        const nodeHeight = GRAPH_NODE_HEIGHT;
        const xGap = GRAPH_X_GAP;
        const yGap = GRAPH_Y_GAP;

        const nodes: NodeData[] = [];
        const levelBaseYCursor = new Map<number, number>();
        const sortedLevels = Array.from(byLevel.keys()).sort((a, b) => a - b);
        sortedLevels.forEach((level) => {
            levelBaseYCursor.set(level, 40);
        });
        const levelSpacing = 44;

        for (const step of steps) {
            const id = Number(step.step_index);
            const level = levelByStep.get(id) || 0;
            const row = (byLevel.get(level) || []).indexOf(id);
            const offset = nodeOffsets[id] || { x: 0, y: 0 };
            const size = estimateStepSize(step);
            const baseY = Number(levelBaseYCursor.get(level) || (40 + row * yGap));
            nodes.push({
                id,
                level,
                row,
                x: 40 + (level + 1) * xGap + Number(offset.x || 0),
                y: baseY + Number(offset.y || 0),
                width: size.width,
                height: size.height,
                step,
            });
            levelBaseYCursor.set(level, baseY + size.height + levelSpacing);
        }

        const entryVisual = buildEntryVisual(entryTreeBundle);
        const entryGroups = entryVisual.groups || [];
        const entryGroupsCount = Math.max(1, Number(entryGroups.length || 0));
        const showEntryModeLabel = entryGroupsCount > 1;
        const groupHeights = entryGroups.map((g) => 54 + Math.max(1, g.leaves.length) * 16);
        const groupsHeight = groupHeights.reduce((acc, h) => acc + h, 0);
        const operatorSpacing = Math.max(0, entryGroupsCount - 1) * 24;
        const groupsStartOffset = showEntryModeLabel ? 46 : 28;
        const gapBeforeSuggestion = 12;
        const suggestionHeight = 48;
        const bottomPadding = 12;
        const suggestionTopOffset =
            groupsStartOffset + groupsHeight + operatorSpacing + gapBeforeSuggestion;
        const entryNodeHeight = Math.max(
            180,
            suggestionTopOffset + suggestionHeight + bottomPadding
        );
        const entryNodeWidth = 270;
        const stepZeroNode = nodes.find((n) => Number(n.id) === 0) || null;
        const entryOffset = nodeOffsets[-1] || { x: 0, y: 0 };
        const entryNode = {
            id: -1,
            x: 40 + Number(entryOffset.x || 0),
            y: stepZeroNode
                ? Math.max(40, Number(stepZeroNode.y) - Math.floor((entryNodeHeight - nodeHeight) / 2)) +
                  Number(entryOffset.y || 0)
                : 40 + Number(entryOffset.y || 0),
            width: entryNodeWidth,
            height: entryNodeHeight,
            visual: entryVisual,
        };
        const minNodeX = nodes.length
            ? Math.min(...nodes.map((n) => n.x), Number(entryNode.x))
            : Number(entryNode.x);
        const minNodeY = nodes.length
            ? Math.min(...nodes.map((n) => n.y), Number(entryNode.y))
            : Number(entryNode.y);
        const shiftX = minNodeX < 40 ? 40 - minNodeX : 0;
        const shiftY = minNodeY < 40 ? 40 - minNodeY : 0;
        if (shiftX || shiftY) {
            for (const n of nodes) {
                n.x += shiftX;
                n.y += shiftY;
            }
            entryNode.x += shiftX;
            entryNode.y += shiftY;
        }
        const entryGroupHitboxes: Array<{
            conditionId: number;
            x: number;
            y: number;
            width: number;
            height: number;
        }> = [];
        let gy = entryNode.y + groupsStartOffset;
        for (let i = 0; i < entryVisual.groups.length; i++) {
            const g = entryVisual.groups[i];
            const gh = 54 + Math.max(1, g.leaves.length) * 16;
            entryGroupHitboxes.push({
                conditionId: Number(g.id),
                x: entryNode.x + 19,
                y: gy,
                width: entryNode.width - 31,
                height: gh,
            });
            gy += gh;
            if (i < entryVisual.groups.length - 1) gy += 24;
        }
        const entrySuggestionHitbox = {
            x: entryNode.x + 19,
            y: entryNode.y + suggestionTopOffset,
            width: entryNode.width - 31,
            height: suggestionHeight,
        };

        const maxNodeRight = nodes.length
            ? Math.max(...nodes.map((n) => n.x + n.width), entryNode.x + entryNode.width)
            : 1200;
        const maxNodeBottom = nodes.length
            ? Math.max(...nodes.map((n) => n.y + n.height), entryNode.y + entryNode.height)
            : 560;
        const baseWidth = Math.max(1200, maxNodeRight + 140);
        const baseHeight = Math.max(560, maxNodeBottom + 140);
        const nodeById = new Map<number, NodeData>(nodes.map((n) => [n.id, n]));

        const edgeLines = edges
            .filter((e) => nodeById.has(Number(e.from_step_index)) && nodeById.has(Number(e.to_step_index)))
            .map((e, idx) => {
                const from = nodeById.get(Number(e.from_step_index))!;
                const to = nodeById.get(Number(e.to_step_index))!;
                const x1 = from.x + from.width;
                const y1 = from.y + from.height / 2;
                const x2 = to.x;
                const y2 = to.y + to.height / 2;
                return {
                    ...e,
                    _idx: idx,
                    x1,
                    y1,
                    x2,
                    y2,
                    labelX: (x1 + x2) / 2,
                    labelY: (y1 + y2) / 2,
                    outgoingCount: outgoing.get(Number(e.from_step_index)) || 0,
                    fromIsBranch:
                        from.step.step_type === "condition" ||
                        from.step.step_type === "wait_event",
                    fromStepType: String(from.step.step_type || ""),
                };
            });

        const editableEdges: EditableEdgeType[] = edgeLines.map((e) => {
            const allowedKeys = allowedEdgeKeysForStepType(String((e as any).fromStepType || ""));
            return {
                id: `edge-${Number(e.from_step_index)}-${Number(e.to_step_index)}-${Number(e.sort_order || 0)}-${Number((e as any)._idx || 0)}`,
                fromStepIndex: Number(e.from_step_index),
                toStepIndex: Number(e.to_step_index),
                sortOrder: Number(e.sort_order || 0),
                edgeKey: String(e.edge_key || "default"),
                x: Number((e as any).labelX || 0),
                y: Number((e as any).labelY || 0),
                editable: allowedKeys.length > 1 || String(e.edge_key || "default") !== "default",
                allowedKeys,
            };
        });
        const insertableEdges: InsertEdgeType[] = edgeLines
            .filter((e: any) => Number(outgoing.get(Number(e.to_step_index)) || 0) > 0)
            .map((e: any) => ({
                id: `insert-${Number(e.from_step_index)}-${Number(e.to_step_index)}-${Number(e.sort_order || 0)}-${Number((e as any)._idx || 0)}`,
                fromStepIndex: Number(e.from_step_index),
                toStepIndex: Number(e.to_step_index),
                sortOrder: Number(e.sort_order || 0),
                edgeKey: String(e.edge_key || "default"),
                x: Number((e as any).labelX || 0),
                y: Number((e as any).labelY || 0),
            }));

        const entryLine = stepZeroNode
            ? {
                  x1: entryNode.x + entryNode.width,
                  y1: entryNode.y + Math.min(entryNode.height - 16, Math.max(24, entryNode.height / 2)),
                  x2: stepZeroNode.x,
                  y2: stepZeroNode.y + stepZeroNode.height / 2,
              }
            : null;

        const suggestions: SuggestionNode[] = [];
        const suggestionLines: Array<{ x1: number; y1: number; x2: number; y2: number; edgeKey: string }> = [];
        const occupied = nodes.map((n) => ({ x: n.x, y: n.y, w: n.width, h: n.height }));
        const overlaps = (x: number, y: number, w: number, h: number) =>
            occupied.some((r) => x < r.x + r.w && x + w > r.x && y < r.y + r.h && y + h > r.y);
        const placeSuggestion = (x: number, y: number) => {
            let tryY = y;
            const step = Math.max(24, Math.floor(nodeHeight * 0.6));
            let guard = 0;
            while (overlaps(x, tryY, nodeWidth, nodeHeight) && guard < 30) {
                tryY += step;
                guard += 1;
            }
            occupied.push({ x, y: tryY, w: nodeWidth, h: nodeHeight });
            return { x, y: tryY };
        };
        const selected = selectedStepIndex != null ? nodeById.get(Number(selectedStepIndex)) : undefined;
        const selectedStep = selected ? selected.step : null;
        if (selected && selectedStep) {
            const existingChildren = edges
                .filter((e) => Number(e.from_step_index) === Number(selected.id))
                .map((e) => nodeById.get(Number(e.to_step_index)))
                .filter(Boolean) as NodeData[];
            const lowestChild =
                existingChildren.length > 0
                    ? existingChildren.slice().sort((a, b) => Number(a.y) - Number(b.y))[existingChildren.length - 1]
                    : null;
            const preferredSuggestionX = selected.x + xGap;
            // Keep suggestions close to parent even if an existing child was dragged too far.
            const baseSuggestionX = lowestChild
                ? Math.min(Number(lowestChild.x), preferredSuggestionX + 180)
                : preferredSuggestionX;
            const baseSuggestionY =
                existingChildren.length > 0
                    ? Math.max(...existingChildren.map((c) => c.y)) + Math.floor(yGap * 0.85)
                    : selected.y;

            if (selectedStep.step_type === "condition" || selectedStep.step_type === "wait_event") {
                const firstKey = selectedStep.step_type === "condition" ? "true" : "matched";
                const secondKey = selectedStep.step_type === "condition" ? "false" : "timeout";
                let nextY = baseSuggestionY;
                const p1 = placeSuggestion(baseSuggestionX, nextY);
                suggestions.push({
                    id: `sg-${selected.id}-${firstKey}-extra-1`,
                    x: p1.x,
                    y: p1.y,
                    edgeKey: firstKey,
                    title: `Click to add (${firstKey})`,
                });
                nextY = p1.y + Math.floor(yGap * 0.85);
                const p2 = placeSuggestion(baseSuggestionX, nextY);
                suggestions.push({
                    id: `sg-${selected.id}-${secondKey}-extra-2`,
                    x: p2.x,
                    y: p2.y,
                    edgeKey: secondKey,
                    title: `Click to add (${secondKey})`,
                });
            } else {
                const p = placeSuggestion(baseSuggestionX, baseSuggestionY);
                suggestions.push({
                    id: `sg-${selected.id}-default`,
                    x: p.x,
                    y: p.y,
                    edgeKey: "default",
                    title: "Click to add",
                });
            }

            for (const sg of suggestions) {
                suggestionLines.push({
                    x1: selected.x + selected.width,
                    y1: selected.y + selected.height / 2,
                    x2: sg.x,
                    y2: sg.y + nodeHeight / 2,
                    edgeKey: sg.edgeKey,
                });
            }
        } else if (nodes.length === 0) {
            // Empty tree (except entry node): suggest the first runnable step coming out of entry condition.
            const firstX = entryNode.x + xGap;
            const firstY = entryNode.y + Math.max(0, Math.floor((entryNode.height - nodeHeight) / 2));
            const p = placeSuggestion(firstX, firstY);
            suggestions.push({
                id: "sg-entry-first",
                x: p.x,
                y: p.y,
                edgeKey: "default",
                title: "Click to add first step",
            });
            suggestionLines.push({
                x1: entryNode.x + entryNode.width,
                y1: entryNode.y + Math.min(entryNode.height - 16, Math.max(24, entryNode.height / 2)),
                x2: p.x,
                y2: p.y + nodeHeight / 2,
                edgeKey: "default",
            });
        }

        const maxSuggestionRight = suggestions.length
            ? Math.max(...suggestions.map((s) => Number(s.x) + nodeWidth))
            : 0;
        const maxSuggestionBottom = suggestions.length
            ? Math.max(...suggestions.map((s) => Number(s.y) + nodeHeight))
            : 0;
        const width = Math.max(baseWidth, maxSuggestionRight + 140);
        const graphBottomPadding = editable ? 140 : 168;
        const height = Math.max(baseHeight, maxSuggestionBottom + graphBottomPadding);

        return {
            nodes,
            nodeWidth,
            nodeHeight,
            width,
            height,
            edgeLines,
            outgoing,
            incoming,
            suggestions,
            suggestionLines,
            entryNode,
            entryLine,
            entryGroupHitboxes,
            entrySuggestionHitbox,
            editableEdges,
            insertableEdges,
        };
    }, [steps, stepIds, edges, nodeOffsets, selectedStepIndex, entryTreeBundle, editable, templates]);

    const branchDimmedStepIds = useMemo(() => {
        const dimmed = new Set<number>();
        if (editable) return dimmed;
        const outgoingByFrom = new Map<number, number[]>();
        const outgoingEdgeByFrom = new Map<number, Array<{ to: number; edgeKey: string }>>();
        for (const e of graph.edgeLines || []) {
            const from = Number((e as any).from_step_index || 0);
            const to = Number((e as any).to_step_index || 0);
            if (!from || !to) continue;
            if (!outgoingByFrom.has(from)) outgoingByFrom.set(from, []);
            outgoingByFrom.get(from)!.push(to);
            if (!outgoingEdgeByFrom.has(from)) outgoingEdgeByFrom.set(from, []);
            outgoingEdgeByFrom.get(from)!.push({
                to,
                edgeKey: String((e as any).edge_key || "default"),
            });
        }

        const dimRoots = new Set<number>();
        for (const [fromKey, selection] of Object.entries(runBranchSelectionByStep || {})) {
            const from = Number(fromKey);
            if (!from || !outgoingByFrom.has(from)) continue;
            const allChildren = outgoingByFrom.get(from) || [];
            if (!allChildren.length) continue;

            const transitionKey = String(selection?.transitionKey || "").trim().toLowerCase();
            if (transitionKey && transitionKey !== "default") {
                const edges = outgoingEdgeByFrom.get(from) || [];
                edges.forEach((edge) => {
                    if (String(edge.edgeKey || "default").toLowerCase() !== transitionKey) dimRoots.add(edge.to);
                });
                continue;
            }

            const chosen = new Set(
                (selection?.chosenNextStepIndexes || [])
                    .map((x) => Number(x || 0))
                    .filter((x) => x > 0)
            );
            if (!chosen.size) continue;
            allChildren.forEach((child) => {
                if (!chosen.has(child)) dimRoots.add(child);
            });
        }

        const q = Array.from(dimRoots);
        while (q.length) {
            const cur = Number(q.shift() || 0);
            if (!cur || dimmed.has(cur)) continue;
            dimmed.add(cur);
            const next = outgoingByFrom.get(cur) || [];
            next.forEach((n) => {
                if (!dimmed.has(n)) q.push(n);
            });
        }
        return dimmed;
    }, [editable, graph.edgeLines, runBranchSelectionByStep]);

    useEffect(() => {
        const canvas = boardCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const zoomScale = Math.max(1, Number(zoom || 1));
        const renderScale = dpr * zoomScale;
        const width = Math.max(1, Math.floor(graph.width));
        const height = Math.max(1, Math.floor(graph.height));
        if (canvas.width !== Math.floor(width * renderScale) || canvas.height !== Math.floor(height * renderScale)) {
            canvas.width = Math.floor(width * renderScale);
            canvas.height = Math.floor(height * renderScale);
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
        }
        ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
        ctx.clearRect(0, 0, width, height);

        const drawEdge = (
            line: { x1: number; y1: number; x2: number; y2: number },
            style: { stroke: string; dashed?: boolean },
            alpha = 1
        ) => {
            ctx.save();
            ctx.globalAlpha = alpha;
            const cp = Math.max(50, Math.abs(line.x2 - line.x1) * 0.42);
            ctx.beginPath();
            ctx.setLineDash(style.dashed ? [4, 3] : []);
            ctx.strokeStyle = style.stroke;
            ctx.lineWidth = 2;
            ctx.moveTo(line.x1, line.y1);
            ctx.bezierCurveTo(line.x1 + cp, line.y1, line.x2 - cp, line.y2, line.x2, line.y2);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.restore();
        };

        const drawLabel = (text: string, x: number, y: number) => {
            const label = String(text || "");
            if (!label || label.toLowerCase() === "default") return;
            const w = 80;
            const h = 16;
            const rx = 6;
            const lx = x - w / 2;
            const ly = y - 18;

            ctx.beginPath();
            ctx.moveTo(lx + rx, ly);
            ctx.lineTo(lx + w - rx, ly);
            ctx.quadraticCurveTo(lx + w, ly, lx + w, ly + rx);
            ctx.lineTo(lx + w, ly + h - rx);
            ctx.quadraticCurveTo(lx + w, ly + h, lx + w - rx, ly + h);
            ctx.lineTo(lx + rx, ly + h);
            ctx.quadraticCurveTo(lx, ly + h, lx, ly + h - rx);
            ctx.lineTo(lx, ly + rx);
            ctx.quadraticCurveTo(lx, ly, lx + rx, ly);
            ctx.closePath();
            ctx.fillStyle = "#ffffff";
            ctx.fill();
            ctx.strokeStyle = "#d6deef";
            ctx.lineWidth = 1;
            ctx.stroke();

            ctx.fillStyle = "#1e3a8a";
            ctx.font = "400 11px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(label, x, y - 10);
        };

        for (const e of graph.edgeLines) {
            const toStep = Number((e as any).to_step_index || 0);
            const edgeAlpha = !editable && branchDimmedStepIds.has(toStep) ? 0.4 : 1;
            drawEdge(e, { stroke: "#8aa4d5" }, edgeAlpha);
            const mx = (e.x1 + e.x2) / 2;
            const my = (e.y1 + e.y2) / 2;
            drawLabel(String(e.edge_key || ""), mx, my);
        }
        if (graph.entryLine) {
            drawEdge(graph.entryLine, { stroke: "#7c97d2" });
        }
        for (const e of graph.suggestionLines) {
            drawEdge(e, { stroke: "#9eb2d8", dashed: true });
            const mx = (e.x1 + e.x2) / 2;
            const my = (e.y1 + e.y2) / 2;
            drawLabel(String(e.edgeKey || ""), mx, my);
        }

        const roundRect = (x: number, y: number, w: number, h: number, r: number) => {
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.quadraticCurveTo(x + w, y, x + w, y + r);
            ctx.lineTo(x + w, y + h - r);
            ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            ctx.lineTo(x + r, y + h);
            ctx.quadraticCurveTo(x, y + h, x, y + h - r);
            ctx.lineTo(x, y + r);
            ctx.quadraticCurveTo(x, y, x + r, y);
            ctx.closePath();
        };

        const drawNodeBox = (
            x: number,
            y: number,
            w: number,
            h: number,
            fill: string,
            border: string,
            shadow: string,
            withGradient = true,
            borderWidth = 1,
            stripColor = "#d7deea"
        ) => {
            ctx.save();
            ctx.shadowColor = shadow;
            ctx.shadowBlur = 8;
            ctx.shadowOffsetY = 2;
            roundRect(x, y, w, h, 12);
            ctx.fillStyle = fill;
            ctx.fill();
            if (withGradient) {
                const gloss = ctx.createLinearGradient(x, y, x + w, y + h);
                gloss.addColorStop(0, "rgba(255,255,255,0.95)");
                gloss.addColorStop(0.58, "rgba(255,255,255,0.92)");
                gloss.addColorStop(1, "rgba(240,246,255,0.95)");
                roundRect(x, y, w, h, 12);
                ctx.fillStyle = gloss;
                ctx.fill();
            }
            roundRect(x, y, w, h, 12);
            ctx.clip();
            ctx.fillStyle = stripColor;
            ctx.fillRect(x, y, 8, h);
            ctx.shadowColor = "transparent";
            if (borderWidth > 0) {
                ctx.lineWidth = borderWidth;
                ctx.strokeStyle = border;
                roundRect(x, y, w, h, 12);
                ctx.stroke();
            }
            ctx.restore();
        };

        const drawStripedOverlay = (x: number, y: number, w: number, h: number) => {
            ctx.save();
            roundRect(x, y, w, h, 12);
            ctx.clip();
            ctx.strokeStyle = "rgba(80, 96, 122, 0.09)";
            ctx.lineWidth = 5;
            const spacing = 20;
            for (let i = -h; i < w + h; i += spacing) {
                ctx.beginPath();
                ctx.moveTo(x + i, y + h);
                ctx.lineTo(x + i + h, y);
                ctx.stroke();
            }
            ctx.restore();
        };

        const nodeStyle = (step: V2AutomationStepDto) => {
            const t = String(step.step_type || "");
            const action = String(step.config?.action_type || "");
            if (t === "wait") return { bg: "#ffffff", strip: "#dfa450", accent: "#8a5b10" };
            if (t === "wait_event") return { bg: "#ffffff", strip: "#a97ce4", accent: "#5a2f94" };
            if (t === "condition") return { bg: "#ffffff", strip: "#789de3", accent: "#254f9d" };
            if (t === "action" && action === "send_whatsapp_template") return { bg: "#ffffff", strip: "#60bf8d", accent: "#1f7f4d" };
            if (t === "action") return { bg: "#ffffff", strip: "#63b6b6", accent: "#0f6767" };
            return { bg: "#ffffff", strip: "#dce4f4", accent: "#35507f" };
        };

        const nodeIconSrc = (step: V2AutomationStepDto) => {
            const t = String(step.step_type || "");
            if (t === "wait") return NodeDelayIcon;
            if (t === "wait_event") return NodeTriggerIcon;
            if (t === "condition") return NodeConditionIcon;
            return NodeActionIcon;
        };

        const getNodeIconImage = (src: string) => {
            let img = nodeIconImagesRef.current[src];
            if (!img) {
                img = new Image();
                img.src = src;
                nodeIconImagesRef.current[src] = img;
            }
            if (!img.complete && !(img as any).__v2OnloadBound) {
                (img as any).__v2OnloadBound = true;
                img.onload = () => setIconRenderVersion((v) => v + 1);
            }
            return img;
        };

        const getMediaPreviewImage = (src: string) => {
            let img = mediaPreviewImagesRef.current[src];
            if (!img) {
                img = new Image();
                img.src = src;
                mediaPreviewImagesRef.current[src] = img;
            }
            if (!img.complete && !(img as any).__v2OnloadBound) {
                (img as any).__v2OnloadBound = true;
                img.onload = () => setIconRenderVersion((v) => v + 1);
            }
            return img;
        };

        const drawTypeBadge = (x: number, y: number, text: string, border: string, bg: string, accent: string) => {
            ctx.save();
            ctx.font = "400 11px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
            const w = Math.max(54, Math.ceil(ctx.measureText(text).width) + 20);
            const h = 20;
            roundRect(x + 19, y - 11, w, h, 10);
            ctx.fillStyle = bg;
            ctx.fill();
            ctx.strokeStyle = border;
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.fillStyle = accent;
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(text, x + 29, y - 1);
            ctx.restore();
        };

            const clampText = (text: string, maxWidth: number): string => {
                const full = String(text || "");
                const ell = "...";
                if (ctx.measureText(full).width <= maxWidth) return full;
                let s = full;
                while (s.length > 0 && ctx.measureText(`${s}${ell}`).width > maxWidth) s = s.slice(0, -1);
                return `${s}${ell}`;
            };

            const drawSecondary = (text: string, x: number, y: number, maxWidth: number) => {
                ctx.fillStyle = "#111111";
                    ctx.font = "500 11px quicksand-medium, Ubuntu, sans-serif";
                ctx.textAlign = "left";
                ctx.textBaseline = "middle";
                ctx.fillText(clampText(text, maxWidth), x, y);
            };

        const drawPrimary = (text: string, x: number, y: number, maxWidth: number) => {
            ctx.fillStyle = "#111111";
            ctx.font = "400 12px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(clampText(text, maxWidth), x, y);
        };

        const drawConditionText = (text: string, x: number, y: number, maxWidth: number) => {
            const words = String(text || "").trim().split(/\s+/).filter(Boolean);
            if (!words.length) return;
            ctx.fillStyle = "#111111";
            ctx.font = "400 12px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";

            const lines: string[] = [];
            let current = "";
            for (const w of words) {
                const candidate = current ? `${current} ${w}` : w;
                if (ctx.measureText(candidate).width <= maxWidth) {
                    current = candidate;
                } else {
                    if (current) lines.push(current);
                    current = w;
                }
                if (lines.length === 2) break;
            }
            if (lines.length < 2 && current) lines.push(current);

            let overflow = false;
            const consumedCount = lines.join(" ").split(/\s+/).filter(Boolean).length;
            if (consumedCount < words.length) overflow = true;

            if (lines.length > 2) lines.length = 2;
            if (overflow && lines.length) {
                let last = lines[lines.length - 1];
                const ell = "...";
                while (last.length > 0 && ctx.measureText(`${last}${ell}`).width > maxWidth) {
                    last = last.slice(0, -1);
                }
                lines[lines.length - 1] = `${last}${ell}`;
            }

            const lineGap = 14;
            const startY = y - ((Math.max(1, lines.length) - 1) * lineGap) / 2;
            lines.forEach((line, idx) => {
                ctx.fillText(line, x, startY + idx * lineGap);
            });
        };

        const buildTemplatePreview = (template: V2TemplateListItemDto | undefined) => {
            if (!template) return null;
            const schema = parseTemplateVariablesSchema((template as any).variables_schema_json);
            const sampleByPath: Record<string, string> = {};
            schema.forEach((p) => {
                sampleByPath[p] = placeholderExampleValue(p);
            });
            const applyVars = (raw: any): string => {
                const text = String(raw || "");
                return text.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_m, p1) => {
                    const key = String(p1 || "").trim();
                    if (sampleByPath[key] != null) return String(sampleByPath[key]);
                    if (sampleByPath[`context.${key}`] != null) return String(sampleByPath[`context.${key}`]);
                    return placeholderExampleValue(key);
                });
            };
            const cfg = parseJsonMaybe((template as any).types_config_json) || {};
            const card = (cfg && (cfg["twilio/card"] || cfg["whatsapp/card"])) || null;
            const textCfg = (cfg && (cfg["twilio/text"] || cfg["whatsapp/text"])) || null;

            const title = applyVars(
                card?.title_text
                || card?.title
                || textCfg?.title_text
                || textCfg?.title
                || template.title
            );
            const body = applyVars(
                card?.body_text
                || card?.body
                || textCfg?.body_text
                || textCfg?.body
                || ""
            );
            let media = "";
            if (card?.image_url) media = applyVars(card.image_url);
            else if (card?.image_var_path) media = applyVars(`{{${String(card.image_var_path)}}}`);
            else if (Array.isArray(card?.media) && card.media.length) media = applyVars(String(card.media[0] || ""));

            return { title, body, media };
        };

        const readOnlySelectedStep = (() => {
            if (editable) return Number(selectedStepIndex);
            const entries = Object.entries(runHistoryByStep || {})
                .map(([k, v]) => ({ step: Number(k), done: !!v?.done, doneAt: String(v?.doneAt || ""), ranAt: String(v?.ranAt || "") }))
                .filter((x) => x.step > 0 && x.done);
            if (entries.length) {
                entries.sort((a, b) => {
                    const ta = new Date(a.doneAt || a.ranAt || 0).getTime();
                    const tb = new Date(b.doneAt || b.ranAt || 0).getTime();
                    return tb - ta;
                });
                return entries[0].step;
            }
            return Number(highlightStepIndex ?? -1);
        })();

        const drawNodeDetails = (n: NodeData) => {
            const runHistory = runHistoryByStep?.[n.id] || null;
            const hasRun = !!runHistory && (!!runHistory.ranAt || !!runHistory.done || !!runHistory.doneAt);
            const isBranchDimmed = !editable && branchDimmedStepIds.has(n.id);
            const baseStyle = nodeStyle(n.step);
            const style = !editable && (hasRun || isBranchDimmed)
                ? { bg: "#eceff3", strip: "#c8ced8", accent: "#5d6573" }
                : baseStyle;
            const isHovered = hoveredNodeId === n.id;
            const isSelected = Number(readOnlySelectedStep) === n.id;
            const h = n.height;
            const actionType = String(n.step.config?.action_type || "send_whatsapp_template");
            const isMissingTemplateAction =
                n.step.step_type === "action" &&
                actionType === "send_whatsapp_template" &&
                !Number(n.step.config?.template_id || 0);
            const isMissingWaitDelay =
                n.step.step_type === "wait" &&
                !Number(n.step.config?.delay_seconds || 0);
            const border = isSelected ? "#c2cbd8" : isHovered ? "#ccd6e5" : "#d3dce8";
            const shadow = isSelected
                ? "rgba(120,130,145,0.2)"
                : isHovered
                ? "rgba(24,50,110,0.16)"
                : "rgba(15,41,97,0.08)";
            const nodeBorderWidth = 1.3;
            ctx.save();
            if (isBranchDimmed && !hasRun) ctx.globalAlpha = 0.4;
            drawNodeBox(n.x, n.y, n.width, h, style.bg, border, shadow, true, nodeBorderWidth, style.strip);
            if (isMissingTemplateAction || isMissingWaitDelay) {
                drawStripedOverlay(n.x, n.y, n.width, h);
            }
            if (isHovered) {
                drawTypeBadge(n.x, n.y, stepLabel(n.step), style.strip, style.bg, style.accent);
            }

            const textX = n.x + 55;
            const maxTextWidth = n.width - 70;
            let y = n.y + 32;
            const iconSrc = nodeIconSrc(n.step);
            if (iconSrc) {
                const iconImg = getNodeIconImage(iconSrc);
                if (iconImg.complete) {
                    ctx.drawImage(iconImg, n.x + 19, n.y + 19, 26, 26);
                }
            }
            if (n.step.step_type === "wait_event") {
                const yTop = n.y + 32;
                drawPrimary(String(n.step.config?.event_name || "event not set"), textX, yTop, maxTextWidth);
                drawSecondary(`Timeout: ${formatFriendlyDuration(n.step.config?.timeout_seconds)}`, textX, yTop + 22, maxTextWidth);
            } else if (n.step.step_type === "wait") {
                drawPrimary(`Delay: ${formatFriendlyDuration(n.step.config?.delay_seconds)}`, textX, y, maxTextWidth);
            } else if (n.step.step_type === "action") {
                let actionSummary = "";
                if (actionType === "send_whatsapp_template") {
                    const templateId = Number(n.step.config?.template_id || 0);
                    const template = templates.find((t) => Number(t.id) === templateId);
                    if (template) {
                        actionSummary = String(template.title || `Template #${templateId}`);
                    } else {
                        actionSummary = "+ Select template";
                    }
                } else if (actionType === "webhook") {
                    actionSummary = "Configure webhook";
                } else if (actionType === "tag_client") {
                    actionSummary = "Configure tag";
                } else if (actionType === "set_property") {
                    actionSummary = "Configure property";
                } else {
                    actionSummary = "Configure action";
                }
                const templateId = Number(n.step.config?.template_id || 0);
                const template = templates.find((t) => Number(t.id) === templateId);
                const preview = actionType === "send_whatsapp_template" ? buildTemplatePreview(template) : null;
                drawPrimary(actionSummary, textX, n.y + 32, maxTextWidth);
                if (preview) {
                    const cardX = n.x + 19;
                    const cardY = n.y + 54;
                    const cardW = n.width - (cardX - n.x) - 12;
                    const cardH = Math.max(44, n.height - 70);
                    roundRect(cardX, cardY, cardW, cardH, 10);
                    const chatBg = ctx.createLinearGradient(cardX, cardY, cardX, cardY + cardH);
                    chatBg.addColorStop(0, "#effaf1");
                    chatBg.addColorStop(1, "#e7f6eb");
                    ctx.fillStyle = chatBg;
                    ctx.fill();
                    ctx.strokeStyle = "rgba(210,218,232,0.95)";
                    ctx.lineWidth = 1;
                    ctx.stroke();

                    // WhatsApp wallpaper overlay.
                    ctx.save();
                    roundRect(cardX, cardY, cardW, cardH, 10);
                    ctx.clip();
                    const wallpaper = getMediaPreviewImage(WhatsAppDefaultWallpaper);
                    if (wallpaper.complete && wallpaper.naturalWidth > 0 && wallpaper.naturalHeight > 0) {
                        ctx.globalAlpha = 0.6;
                        const scale = Math.max(cardW / wallpaper.naturalWidth, cardH / wallpaper.naturalHeight);
                        const dw = wallpaper.naturalWidth * scale;
                        const dh = wallpaper.naturalHeight * scale;
                        const dx = cardX + (cardW - dw) / 2;
                        const dy = cardY + (cardH - dh) / 2;
                        ctx.drawImage(wallpaper, dx, dy, dw, dh);
                    } else {
                        // Fallback texture until wallpaper asset is loaded.
                        ctx.strokeStyle = "rgba(87, 141, 108, 0.22)";
                        ctx.lineWidth = 1;
                        for (let px = cardX - 24; px < cardX + cardW + 24; px += 20) {
                            ctx.beginPath();
                            ctx.moveTo(px, cardY);
                            ctx.lineTo(px + 22, cardY + cardH);
                            ctx.stroke();
                        }
                    }
                    ctx.restore();

                    const contentPad = 10;
                    const contentX = cardX + contentPad;
                    const contentW = cardW - contentPad * 2;
                    let bodyY = cardY + 16;
                    if (preview.media) {
                        const mediaX = contentX;
                        const mediaY = cardY + contentPad;
                        const mediaW = contentW;
                        const footerReserve = 16;
                        const maxMediaFromCard = Math.max(28, cardH - (contentPad * 2) - footerReserve - 12);
                        const mediaH =
                            maxMediaFromCard < 92
                                ? maxMediaFromCard
                                : Math.max(92, Math.min(140, maxMediaFromCard));
                        roundRect(mediaX, mediaY, mediaW, mediaH, 8);
                        ctx.fillStyle = "rgba(244,248,255,0.98)";
                        ctx.fill();
                        ctx.strokeStyle = "rgba(210,218,232,0.95)";
                        ctx.lineWidth = 1;
                        ctx.stroke();

                        const canDrawRealImage = /^https?:\/\//i.test(String(preview.media || ""));
                        if (canDrawRealImage) {
                            const mediaImg = getMediaPreviewImage(String(preview.media));
                            if (mediaImg.complete && mediaImg.naturalWidth > 0 && mediaImg.naturalHeight > 0) {
                                ctx.save();
                                roundRect(mediaX, mediaY, mediaW, mediaH, 8);
                                ctx.clip();
                                const scale = Math.max(mediaW / mediaImg.naturalWidth, mediaH / mediaImg.naturalHeight);
                                const dw = mediaImg.naturalWidth * scale;
                                const dh = mediaImg.naturalHeight * scale;
                                const dx = mediaX + (mediaW - dw) / 2;
                                const dy = mediaY + (mediaH - dh) / 2;
                                ctx.drawImage(mediaImg, dx, dy, dw, dh);
                                ctx.restore();
                            } else {
                                ctx.fillStyle = "#6f7d95";
                                ctx.font = "500 12px quicksand-medium, Ubuntu, sans-serif";
                                ctx.textAlign = "center";
                                ctx.textBaseline = "middle";
                                ctx.fillText("Image loading...", mediaX + mediaW / 2, mediaY + mediaH / 2);
                            }
                        } else {
                            ctx.fillStyle = "#6f7d95";
                            ctx.font = "500 12px quicksand-medium, Ubuntu, sans-serif";
                            ctx.textAlign = "center";
                            ctx.textBaseline = "middle";
                            ctx.fillText("Image preview", mediaX + mediaW / 2, mediaY + mediaH / 2);
                        }
                        bodyY = Math.min(cardY + cardH - footerReserve, mediaY + mediaH + 14);
                    }
                    const bodyText = String(preview.body || preview.title || "").replace(/\s+/g, " ").trim();
                    drawSecondary(bodyText || "Template preview", contentX, bodyY, contentW);
                }
            } else if (n.step.step_type === "condition") {
                drawPrimary("Condition", textX, y, maxTextWidth);
            }
            if (!editable && hasRun) {
                const whenText = new Date(String(runHistory?.ranAt || "")).toLocaleString();
                const centerX = n.x + Math.floor(n.width / 2);
                ctx.fillStyle = "#6b7280";
                ctx.font = "500 10px quicksand-medium, Ubuntu, sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(`Ran: ${whenText}`, centerX, n.y + h + 13);
            }
            ctx.restore();
        };

        for (const n of graph.nodes) drawNodeDetails(n);

        {
            const en = graph.entryNode;
            const isEntryHovered = hoveredNodeId === -1 || hoveredEntryGroupId != null;
            const entryBorder = isEntryHovered ? "#ccd6e5" : "#d3dce8";
            const entryShadow = isEntryHovered ? "rgba(24,50,110,0.16)" : "rgba(15,41,97,0.08)";
            const entryStrip = editable ? "#a97ce4" : "#c8ced8";
            drawNodeBox(
                en.x,
                en.y,
                en.width,
                en.height,
                "#ffffff",
                entryBorder,
                entryShadow,
                true,
                1.3,
                entryStrip
            );
            if (isEntryHovered) {
                drawTypeBadge(en.x, en.y, "Entry condition", entryStrip, "#ffffff", "#5a2f94");
            }
            if (!entryConditionId) {
                drawPrimary(
                    "Setup required",
                    en.x + 20,
                    en.y + 36,
                    en.width - 40
                );
            } else {
                const visual = en.visual || { mode: "AND", groups: [] };
                const showEntryModeLabel = (visual.groups?.length || 0) > 1;
                let y = en.y + (showEntryModeLabel ? 28 : 16);
                const showModeSelect = showEntryModeLabel && (hoveredNodeId === -1 || hoveredEntryGroupId != null);
                if (showEntryModeLabel) {
                    ctx.fillStyle = "#3f4f67";
                    ctx.font = "400 14px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
                    ctx.textAlign = "left";
                    ctx.textBaseline = "middle";
                    ctx.fillText(showModeSelect ? "Mode:" : `Mode: ${visual.mode}`, en.x + 19, y);
                    y += 18;
                }

                for (let gi = 0; gi < visual.groups.length; gi++) {
                    const g = visual.groups[gi];
                    const gh = 54 + Math.max(1, g.leaves.length) * 16;
                    const isGroupHovered = Number(hoveredEntryGroupId) === Number(g.id);
                    roundRect(en.x + 19, y, en.width - 31, gh, 8);
                    ctx.fillStyle = isGroupHovered ? "rgba(210,224,248,0.22)" : "rgba(210,224,248,0.12)";
                    ctx.fill();
                    if (isGroupHovered) {
                        ctx.strokeStyle = "#ccd6e5";
                        ctx.lineWidth = 1;
                        ctx.stroke();
                    }

                    const groupIcon = getNodeIconImage(NodeTriggerIcon);
                    if (groupIcon.complete) {
                        ctx.drawImage(groupIcon, en.x + 31, y + 10, 26, 26);
                    }
                    ctx.textAlign = "left";
                    ctx.textBaseline = "middle";
                    ctx.fillStyle = "#2d3f62";
                    ctx.font = "400 13px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
                    ctx.fillText(String(g.title || `Group ${gi + 1}`), en.x + 67, y + 22);

                    let ly = y + 44;
                    ctx.fillStyle = "#2d2d2d";
                    ctx.font = "500 11px quicksand-medium, Ubuntu, sans-serif";
                    for (const leaf of g.leaves) {
                        const leafText = String(leaf || "");
                        const maxLeafW = en.width - 97;
                        let s = leafText;
                        while (s.length > 0 && ctx.measureText(s).width > maxLeafW) s = `${s.slice(0, -1)}...`;
                        ctx.fillText(`- ${s}`, en.x + 67, ly);
                        ly += 16;
                    }

                    y += gh;
                    if (gi < visual.groups.length - 1) {
                        const opText = String(visual.mode || "AND");
                        ctx.fillStyle = entryStrip;
                        ctx.font = "400 12px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
                        ctx.textAlign = "left";
                        ctx.textBaseline = "middle";
                        // keep 12px top + 12px bottom spacing around the connector operator
                        ctx.fillText(opText, en.x + 22, y + 12);
                        y += 24;
                    }
                }
                if (editable) {
                    const sg = graph.entrySuggestionHitbox;
                    const isEntrySuggestionHovered = hoveredSuggestionId === "entry-add-group";
                    roundRect(sg.x, sg.y, sg.width, sg.height, 8);
                    ctx.fillStyle = isEntrySuggestionHovered ? "#ffffff" : "rgba(255,255,255,0.32)";
                    ctx.fill();
                    ctx.setLineDash([4, 3]);
                    ctx.strokeStyle = isEntrySuggestionHovered ? "#2967ff" : "#6a6457";
                    ctx.lineWidth = 1;
                    ctx.stroke();
                    ctx.setLineDash([]);
                    ctx.fillStyle = isEntrySuggestionHovered ? "#2967ff" : "#3b372d";
                    ctx.font = "400 13px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.fillText("+ Add condition group", sg.x + sg.width / 2, sg.y + sg.height / 2);
                }
            }
        }

        for (const s of graph.suggestions) {
            const isHovered = hoveredSuggestionId === s.id;
            drawNodeBox(
                s.x,
                s.y,
                graph.nodeWidth,
                graph.nodeHeight - 2,
                isHovered ? "rgba(255,255,255,0.34)" : "rgba(255,255,255,0.2)",
                isHovered ? "#6f8fcf" : "#9bb2dc",
                "rgba(15,41,97,0)",
                false
            );
            ctx.setLineDash([5, 4]);
            roundRect(s.x, s.y, graph.nodeWidth, graph.nodeHeight - 2, 12);
            ctx.strokeStyle = "#9bb2dc";
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = "#35507f";
            ctx.font = "400 13px quicksand-bold, Ubuntu-bold, Ubuntu, sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const label = `+ ${String(s.title || "").replace(/^\+\s*/, "")}`;
            ctx.fillText(label, s.x + Math.floor(graph.nodeWidth / 2), s.y + Math.floor((graph.nodeHeight - 2) / 2));
        }
    }, [graph, hoveredNodeId, hoveredSuggestionId, hoveredEntryGroupId, selectedStepIndex, templates, zoom, entryConditionId, editable, highlightStepIndex, runHistoryByStep, runBranchSelectionByStep, branchDimmedStepIds, iconRenderVersion]);

    useEffect(() => {
        if (!editable) return;

        const onMouseMove = (ev: MouseEvent) => {
            const dragState = dragRef.current;
            if (!dragState) return;
            const dx = ev.clientX - dragState.startX;
            const dy = ev.clientY - dragState.startY;
            if (Math.abs(dx) > 2 || Math.abs(dy) > 2) dragState.moved = true;
            setNodeOffsets((prev) => ({
                ...prev,
                [dragState.stepId]: {
                    x: dragState.originX + dx,
                    y: dragState.originY + dy,
                },
            }));
        };

        const onMouseUp = () => {
            const dragState = dragRef.current;
            if (!dragState) return;
            if (dragState.moved) {
                setSuppressClickForStepId(dragState.stepId);
                window.setTimeout(() => setSuppressClickForStepId((prev) => (prev === dragState.stepId ? null : prev)), 120);
            } else if (!dragState.wasSelectedBefore) {
                // first click on a non-selected node should select-only (no modal open)
                setSuppressClickForStepId(dragState.stepId);
                window.setTimeout(() => setSuppressClickForStepId((prev) => (prev === dragState.stepId ? null : prev)), 120);
            }
            setDraggingStepId(null);
            dragRef.current = null;
        };

        const onMouseDownCapture = (ev: MouseEvent) => {
            const target = ev.target as HTMLElement;
            const holder = target.closest("[data-step-id]");
            if (!holder) return;
            if (target.closest(".ant-btn, input, textarea, .ant-select, .ant-select-dropdown")) return;
            const raw = holder.getAttribute("data-step-id");
            const stepId = Number(raw);
            if (!Number.isFinite(stepId)) return;
            if (stepId < 0) return; // keep entry node fixed
            const origin = offsetsRef.current[stepId] || { x: 0, y: 0 };
            const wasSelectedBefore = Number(selectedStepIndex) === stepId;
            dragRef.current = {
                stepId,
                startX: ev.clientX,
                startY: ev.clientY,
                originX: origin.x,
                originY: origin.y,
                moved: false,
                wasSelectedBefore,
            };
            if (!wasSelectedBefore) setSelectedStepIndex(stepId);
            setDraggingStepId(stepId);
        };

        window.addEventListener("mousedown", onMouseDownCapture, true);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousedown", onMouseDownCapture, true);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [selectedStepIndex, editable]);

    const selectedStep = useMemo(
        () => steps.find((s) => Number(s.step_index) === Number(selectedStepIndex)) || null,
        [steps, selectedStepIndex]
    );

    const selectedOutgoing = useMemo(
        () =>
            edges
                .filter((e) => Number(e.from_step_index) === Number(selectedStepIndex))
                .slice()
                .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)),
        [edges, selectedStepIndex]
    );

    const isBranchStep = (step: V2AutomationStepDto | null) =>
        !!step && (step.step_type === "condition" || step.step_type === "wait_event");

    const nextEdgeKeyForStep = (step: V2AutomationStepDto | null, outgoingCount: number) => {
        if (!step) return "default";
        if (step.step_type === "condition") return outgoingCount === 0 ? "true" : outgoingCount === 1 ? "false" : `edge_${outgoingCount + 1}`;
        if (step.step_type === "wait_event") return outgoingCount === 0 ? "matched" : outgoingCount === 1 ? "timeout" : `edge_${outgoingCount + 1}`;
        return "default";
    };

    const openNodeModal = (stepIndex: number, preserveSelection = false) => {
        const step = steps.find((s) => Number(s.step_index) === stepIndex);
        if (!step) return;
        if (!preserveSelection) setSelectedStepIndex(stepIndex);
        modalForm.setFieldsValue({
            step_type: step.step_type,
            delay_seconds: step.config?.delay_seconds ?? 60,
            event_name: step.config?.event_name || "",
            poll_seconds: step.config?.poll_seconds ?? 30,
            timeout_seconds: step.config?.timeout_seconds ?? 0,
            entity_linkage_mode: step.config?.entity_linkage_mode || "any",
            entity_type: step.config?.entity_type || "",
            entity_id: step.config?.entity_id || "",
            condition_id: step.config?.condition_id ?? null,
            condition_scope_mode: step.config?.condition_scope_mode || "since_run_start",
            condition_window_seconds: step.config?.condition_window_seconds ?? 86400,
            action_type: step.config?.action_type || "send_whatsapp_template",
            template_id: step.config?.template_id ?? null,
            missing_variable_policy: step.config?.missing_variable_policy || "skip_step",
            webhook_url: step.config?.webhook_url || "",
            webhook_method: step.config?.method || "POST",
            webhook_body_json:
                step.config?.body_json != null
                    ? JSON.stringify(step.config?.body_json, null, 2)
                    : "",
            tag_op: step.config?.op || "add",
            tag_value: step.config?.tag || "",
            property_key: step.config?.property_key || "",
            property_value_text:
                step.config?.value_text != null
                    ? String(step.config?.value_text)
                    : step.config?.value != null
                    ? String(step.config?.value)
                    : step.config?.value_json != null
                    ? JSON.stringify(step.config?.value_json)
                    : "",
        });
        const rawTree = step.config?.condition_tree_json || step.config?.condition_tree || null;
        let nextMode: ConditionGroupMode = "ALL_GROUPS";
        let nextGroups: ConditionGroup[] = [];
        if (rawTree && typeof rawTree === "object" && Array.isArray(rawTree.groups)) {
            nextMode = String(rawTree.mode || "ALL_GROUPS") === "ANY_GROUP" ? "ANY_GROUP" : "ALL_GROUPS";
            nextGroups = rawTree.groups
                .map((g: any) => ({
                    id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                    rules: Array.isArray(g?.rules)
                        ? g.rules.map((r: any) => ({
                              id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                              field: String(r?.field || ""),
                              operator: String(r?.operator || "equals"),
                              value: String(r?.value ?? ""),
                              count_threshold:
                                  r?.count_threshold == null || r?.count_threshold === ""
                                      ? null
                                      : Number(r?.count_threshold),
                          }))
                        : [],
                }))
                .filter((g: ConditionGroup) => g.rules.length > 0);
        } else if (Array.isArray(rawTree)) {
            // Backward compatibility for old flat rows.
            nextMode = "ALL_GROUPS";
            nextGroups = [
                {
                    id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                    rules: rawTree.map((r: any) => ({
                        id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                        field: String(r?.field || ""),
                        operator: String(r?.operator || "equals"),
                        value: String(r?.value ?? ""),
                        count_threshold:
                            r?.count_threshold == null || r?.count_threshold === ""
                                ? null
                                : Number(r?.count_threshold),
                    })),
                },
            ];
        }
        setConditionGroupMode(nextMode);
        setConditionGroups(nextGroups.length ? nextGroups : [makeConditionGroup()]);
        setModalOpen(true);
    };

    const updateConditionRule = (groupId: string, ruleId: string, patch: Partial<ConditionRuleRow>) => {
        setConditionGroups((prev) =>
            prev.map((g) =>
                g.id === groupId
                    ? { ...g, rules: g.rules.map((r) => (r.id === ruleId ? { ...r, ...patch } : r)) }
                    : g
            )
        );
    };

    const addConditionRule = (groupId: string) => {
        setConditionGroups((prev) =>
            prev.map((g) => (g.id === groupId ? { ...g, rules: [...g.rules, makeConditionRuleRow()] } : g))
        );
    };

    const removeConditionRule = (groupId: string, ruleId: string) => {
        setConditionGroups((prev) =>
            prev.map((g) => {
                if (g.id !== groupId) return g;
                const nextRules = g.rules.filter((r) => r.id !== ruleId);
                return { ...g, rules: nextRules.length ? nextRules : [makeConditionRuleRow()] };
            })
        );
    };

    const addConditionGroup = () => {
        setConditionGroups((prev) => [...prev, makeConditionGroup()]);
    };

    const removeConditionGroup = (groupId: string) => {
        setConditionGroups((prev) => {
            const next = prev.filter((g) => g.id !== groupId);
            return next.length ? next : [makeConditionGroup()];
        });
    };

    const conditionSummary = useMemo(() => {
        const groups = conditionGroups
            .map((g) =>
                g.rules
                    .map((r) => {
                        let op = String(r.operator || "equals");
                        if (
                            String(r.field || "") === "event_name" &&
                            ["gte", "gt", "equals", "lte", "lt"].includes(op) &&
                            r.count_threshold != null
                        ) {
                            const m: Record<string, string> = {
                                gte: "count_gte",
                                gt: "count_gt",
                                equals: "count_eq",
                                lte: "count_lte",
                                lt: "count_lt",
                            };
                            op = m[op] || op;
                        }
                        if (op.startsWith("count_")) {
                            const labelMap: Record<string, string> = {
                                count_gte: ">=",
                                count_gt: ">",
                                count_eq: "=",
                                count_lte: "<=",
                                count_lt: "<",
                            };
                            const cmp = labelMap[op] || ">=";
                            return `${r.field || "?"} count ${cmp} ${Number(r.count_threshold ?? 0)} for "${String(r.value || "").trim() || "?"}"`;
                        }
                        return `${r.field || "?"} ${op} "${String(r.value || "").trim() || "?"}"`;
                    })
                    .join(" AND ")
            )
            .filter((txt) => txt.trim().length > 0);
        if (!groups.length) return "No condition defined yet.";
        const glue = conditionGroupMode === "ANY_GROUP" ? " OR " : " AND ";
        return groups.map((g) => `(${g})`).join(glue);
    }, [conditionGroups, conditionGroupMode]);

    const selectOrOpenNode = (stepIndex: number) => {
        if (Number(selectedStepIndex) !== Number(stepIndex)) {
            setSelectedStepIndex(stepIndex);
            return;
        }
        openNodeModal(stepIndex);
    };

    const updateStep = (stepIndex: number, patch: Partial<V2AutomationStepDto>) => {
        setSteps((prev) =>
            prev
                .map((s) => (Number(s.step_index) === stepIndex ? { ...s, ...patch } : s))
                .sort((a, b) => Number(a.step_index) - Number(b.step_index))
        );
    };

    const addChildFromSelected = (forcedEdgeKey?: string, forcedPos?: { x: number; y: number }) => {
        const parentStepIndex = selectedStepIndex;
        const next = steps.length ? Math.max(...steps.map((s) => Number(s.step_index))) + 1 : 0;
        const newStep = buildDefaultStep(next, "action");

        if (!steps.length) {
            setSteps([newStep]);
            setSelectedStepIndex(next);
            openNodeModal(next);
            return;
        }

        if (selectedStepIndex == null || Number(selectedStepIndex) < 0) {
            message.info("Select a node first, then add a child node.");
            return;
        }

        const parent = steps.find((s) => Number(s.step_index) === Number(selectedStepIndex)) || null;
        const parentOutgoing = edges
            .filter((e) => Number(e.from_step_index) === Number(selectedStepIndex))
            .slice()
            .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

        const key = String(forcedEdgeKey || nextEdgeKeyForStep(parent, parentOutgoing.length));
        const nextSteps = [...steps, newStep].sort((a, b) => Number(a.step_index) - Number(b.step_index));
        setSteps(nextSteps);
        setEdges((prev) => [
            ...prev,
            {
                from_step_index: Number(selectedStepIndex),
                to_step_index: next,
                edge_key: key,
                sort_order: parentOutgoing.length,
            },
        ]);

        const parentNode = graph.nodes.find((n) => Number(n.id) === Number(parentStepIndex));
        if (parentNode) {
            const targetLevel = parentNode.level + 1;
            const siblings = edges
                .filter((e) => Number(e.from_step_index) === Number(parentNode.id))
                .map((e) => graph.nodes.find((n) => Number(n.id) === Number(e.to_step_index)))
                .filter(Boolean) as NodeData[];
            const lowestSibling =
                siblings.length > 0
                    ? siblings.slice().sort((a, b) => Number(a.y) - Number(b.y))[siblings.length - 1]
                    : null;
            const targetX =
                forcedPos?.x ??
                (lowestSibling ? Number(lowestSibling.x) : parentNode.x + GRAPH_X_GAP);
            const isFirstChild = siblings.length === 0;
            const targetY =
                isFirstChild
                    ? parentNode.y
                    : (forcedPos?.y ??
                (siblings.length > 0
                    ? Math.max(...siblings.map((s) => s.y)) + Math.floor(GRAPH_Y_GAP * 0.85)
                    : parentNode.y));

            const countAtTargetLevel = graph.nodes.filter((n) => Number(n.level) === Number(targetLevel)).length;
            // Must match graph layout formula: x = 40 + (level + 1) * GRAPH_X_GAP + offset.x
            const baseX = 40 + (targetLevel + 1) * GRAPH_X_GAP;
            const baseY = 40 + countAtTargetLevel * GRAPH_Y_GAP;
            setNodeOffsets((prev) => ({
                ...prev,
                [next]: {
                    x: Math.round(targetX - baseX),
                    y: Math.round(targetY - baseY),
                },
            }));
        }

        setSelectedStepIndex(next);
        window.setTimeout(() => openNodeModal(next), 0);
    };

    const addStep = () => addChildFromSelected();

    const insertNodeBetween = (edge: InsertEdgeType) => {
        const fromNode = graph.nodes.find((n) => Number(n.id) === Number(edge.fromStepIndex));
        const toNode = graph.nodes.find((n) => Number(n.id) === Number(edge.toStepIndex));
        if (!fromNode || !toNode) return;

        const next = steps.length ? Math.max(...steps.map((s) => Number(s.step_index))) + 1 : 0;
        const newStep = buildDefaultStep(next, "action");
        const from = Number(edge.fromStepIndex);
        const to = Number(edge.toStepIndex);
        const oldSort = Number(edge.sortOrder || 0);
        const oldEdgeKey = String(edge.edgeKey || "default");

        setSteps((prev) => [...prev, newStep].sort((a, b) => Number(a.step_index) - Number(b.step_index)));
        setEdges((prev) => {
            const nextEdges = [...prev];
            const removeIdx = nextEdges.findIndex(
                (e) =>
                    Number(e.from_step_index) === from &&
                    Number(e.to_step_index) === to &&
                    Number(e.sort_order || 0) === oldSort &&
                    String(e.edge_key || "default") === oldEdgeKey
            );
            if (removeIdx >= 0) {
                nextEdges.splice(removeIdx, 1);
            } else {
                const fallbackIdx = nextEdges.findIndex(
                    (e) =>
                        Number(e.from_step_index) === from &&
                        Number(e.to_step_index) === to &&
                        String(e.edge_key || "default") === oldEdgeKey
                );
                if (fallbackIdx >= 0) nextEdges.splice(fallbackIdx, 1);
            }
            nextEdges.push(
                {
                    from_step_index: from,
                    to_step_index: next,
                    edge_key: oldEdgeKey,
                    sort_order: oldSort,
                },
                {
                    from_step_index: next,
                    to_step_index: to,
                    edge_key: "default",
                    sort_order: 0,
                }
            );
            return nextEdges;
        });

        const targetLevel = fromNode.level + 1;
        const targetX = Math.round((Number(fromNode.x) + Number(toNode.x)) / 2);
        const targetY = Math.round((Number(fromNode.y) + Number(toNode.y)) / 2);
        const countAtTargetLevel = graph.nodes.filter((n) => Number(n.level) === Number(targetLevel)).length;
        const baseX = 40 + (targetLevel + 1) * GRAPH_X_GAP;
        const baseY = 40 + countAtTargetLevel * GRAPH_Y_GAP;
        setNodeOffsets((prev) => ({
            ...prev,
            [next]: {
                x: Math.round(targetX - baseX),
                y: Math.round(targetY - baseY),
            },
        }));

        setSelectedStepIndex(next);
        window.setTimeout(() => openNodeModal(next), 0);
    };

    const computeCascadeDeleteSet = (rootStepIndex: number): Set<number> => {
        const outgoingByFrom = new Map<number, number[]>();
        const incomingByTo = new Map<number, number[]>();
        for (const e of edges) {
            const from = Number(e.from_step_index);
            const to = Number(e.to_step_index);
            const out = outgoingByFrom.get(from) || [];
            out.push(to);
            outgoingByFrom.set(from, out);
            const inc = incomingByTo.get(to) || [];
            inc.push(from);
            incomingByTo.set(to, inc);
        }

        const reachable = new Set<number>();
        const q: number[] = [Number(rootStepIndex)];
        while (q.length) {
            const cur = Number(q.shift());
            const outs = outgoingByFrom.get(cur) || [];
            for (const to of outs) {
                if (reachable.has(to)) continue;
                reachable.add(to);
                q.push(to);
            }
        }

        const toDelete = new Set<number>([Number(rootStepIndex)]);
        let changed = true;
        while (changed) {
            changed = false;
            for (const nodeId of Array.from(reachable)) {
                if (toDelete.has(nodeId)) continue;
                const incoming = incomingByTo.get(nodeId) || [];
                if (!incoming.length) continue;
                if (incoming.every((parentId) => toDelete.has(Number(parentId)))) {
                    toDelete.add(nodeId);
                    changed = true;
                }
            }
        }
        return toDelete;
    };

    const removeSteps = (stepIndices: number[]) => {
        const set = new Set(stepIndices.map((x) => Number(x)));
        const removedNodes = graph.nodes
            .filter((n) => set.has(Number(n.id)))
            .sort((a, b) => Number(a.y) - Number(b.y));
        const siblingsByLevel = new Map<number, NodeData[]>();
        for (const rn of removedNodes) {
            if (!siblingsByLevel.has(Number(rn.level))) {
                siblingsByLevel.set(
                    Number(rn.level),
                    graph.nodes
                        .filter((n) => Number(n.level) === Number(rn.level) && !set.has(Number(n.id)))
                        .sort((a, b) => Number(a.y) - Number(b.y))
                );
            }
        }

        setSteps((prev) => prev.filter((s) => !set.has(Number(s.step_index))));
        setEdges((prev) =>
            prev.filter((e) => !set.has(Number(e.from_step_index)) && !set.has(Number(e.to_step_index)))
        );
        setNodeOffsets((prev) => {
            const next = { ...prev };
            for (const stepIndex of Array.from(set)) {
                delete next[Number(stepIndex)];
            }
            for (const removed of removedNodes) {
                const siblings = siblingsByLevel.get(Number(removed.level)) || [];
                const nextSibling = siblings.find((s) => Number(s.y) > Number(removed.y));
                if (!nextSibling) continue;
                const shiftY = Number(removed.y) - Number(nextSibling.y);
                if (!shiftY) continue;
                for (const sib of siblings) {
                    if (Number(sib.y) >= Number(nextSibling.y)) {
                        const prevOffset = next[Number(sib.id)] || { x: 0, y: 0 };
                        next[Number(sib.id)] = {
                            x: Number(prevOffset.x || 0),
                            y: Number(prevOffset.y || 0) + shiftY,
                        };
                    }
                }
            }
            return next;
        });
        if (selectedStepIndex != null && set.has(Number(selectedStepIndex))) {
            setModalOpen(false);
            setSelectedStepIndex(null);
        }
    };

    const confirmDeleteStep = (stepIndex: number) => {
        const toDelete = computeCascadeDeleteSet(stepIndex);
        const dependentCount = Math.max(0, toDelete.size - 1);
        showToast({
            skin: ToastSkin.danger,
            text:
                dependentCount > 0
                    ? `This action is irreversible. Deleting this node will also delete ${dependentCount} dependent child node${dependentCount > 1 ? "s" : ""}.`
                    : "This action is irreversible.",
            buttons: [
                {
                    text: "Delete",
                    onClick: () => {
                        closeToast();
                        removeSteps(Array.from(toDelete));
                    },
                },
                { text: "Cancel", onClick: closeToast },
            ],
        });
    };

    const confirmPublishTree = () => {
        showToast({
            skin: ToastSkin.neutral,
            text: "This will bring the latest automation changes to production.",
            buttons: [
                {
                    text: "Publish",
                    onClick: async () => {
                        closeToast();
                        await publishAll();
                    },
                },
                { text: "Cancel", onClick: closeToast },
            ],
        });
    };

    const upsertOutgoing = (fromStepIndex: number, nextOutgoing: V2AutomationEdgeDto[]) => {
        const normalized = nextOutgoing.map((e, i) => ({
            ...e,
            from_step_index: fromStepIndex,
            sort_order: i,
        }));
        setEdges((prev) => [...prev.filter((e) => Number(e.from_step_index) !== fromStepIndex), ...normalized]);
    };

    const addOutgoing = () => {
        if (selectedStepIndex == null) return;
        const targets = stepIds.filter((s) => Number(s) !== Number(selectedStepIndex));
        if (!targets.length) return;
        const cur = selectedOutgoing.slice();
        cur.push({
            from_step_index: Number(selectedStepIndex),
            to_step_index: targets[0],
            edge_key: nextEdgeKeyForStep(selectedStep, cur.length),
            sort_order: cur.length,
        });
        upsertOutgoing(Number(selectedStepIndex), cur);
    };

    const updateOutgoing = (rowIdx: number, patch: Partial<V2AutomationEdgeDto>) => {
        if (selectedStepIndex == null) return;
        const cur = selectedOutgoing.slice();
        if (!cur[rowIdx]) return;
        cur[rowIdx] = { ...cur[rowIdx], ...patch };
        upsertOutgoing(Number(selectedStepIndex), cur);
    };

    const removeOutgoing = (rowIdx: number) => {
        if (selectedStepIndex == null) return;
        const cur = selectedOutgoing.filter((_, idx) => idx !== rowIdx);
        upsertOutgoing(Number(selectedStepIndex), cur);
    };

    const applyEdgeKeyChange = async () => {
        if (!edgeEditTarget) return;
        const nextKey = normalizeEdgeKeyForStepType(
            steps.find((s) => Number(s.step_index) === Number(edgeEditTarget.fromStepIndex))?.step_type || "",
            edgeEditKey
        );
        setEdges((prev) =>
            prev.map((e) =>
                Number(e.from_step_index) === Number(edgeEditTarget.fromStepIndex) &&
                Number(e.to_step_index) === Number(edgeEditTarget.toStepIndex) &&
                Number(e.sort_order || 0) === Number(edgeEditTarget.sortOrder)
                    ? { ...e, edge_key: nextKey }
                    : e
            )
        );
        setEdgeEditOpen(false);
        setEdgeEditTarget(null);
    };

    const saveNodeModal = async () => {
        if (selectedStepIndex == null) return;
        const existingStep = steps.find((s) => Number(s.step_index) === Number(selectedStepIndex)) || null;
        const values = await modalForm.validateFields();
        const stepType = values.step_type as StepType;

        if (stepType === "action") {
            const actionType = String(values.action_type || "send_whatsapp_template");
            if (!["send_whatsapp_template", "webhook", "tag_client", "set_property"].includes(actionType)) {
                message.error("Unsupported action type");
                return;
            }
        }

        const nextStep = buildDefaultStep(Number(selectedStepIndex), stepType);
        if (stepType === "wait") nextStep.config.delay_seconds = Number(values.delay_seconds || 0);
        if (stepType === "condition") {
            const cleanedGroups = conditionGroups
                .map((g) => ({
                    rules: g.rules
                        .map((r) => {
                            const field = String(r.field || "").trim();
                            let operator = String(r.operator || "equals").trim();
                            const value = String(r.value ?? "").trim();
                            const out: any = { field, operator, value };
                            // Backward-friendly: if user picked numeric op on event_name and filled threshold,
                            // convert automatically into count_* operator.
                            if (
                                field === "event_name" &&
                                ["gte", "gt", "equals", "lte", "lt"].includes(operator) &&
                                r.count_threshold != null
                            ) {
                                const m: Record<string, string> = {
                                    gte: "count_gte",
                                    gt: "count_gt",
                                    equals: "count_eq",
                                    lte: "count_lte",
                                    lt: "count_lt",
                                };
                                operator = m[operator] || operator;
                                out.operator = operator;
                            }
                            if (operator.startsWith("count_")) {
                                out.count_threshold = Math.max(0, Number(r.count_threshold || 0));
                            }
                            return out;
                        })
                        .filter((r) => r.field && r.operator && r.value.length > 0),
                }))
                .filter((g) => g.rules.length > 0);
            nextStep.config.condition_id = null;
            nextStep.config.condition_tree_json = {
                mode: conditionGroupMode,
                groups: cleanedGroups,
            };
            nextStep.config.condition_scope_mode = String(values.condition_scope_mode || "since_run_start");
            if (nextStep.config.condition_scope_mode === "rolling_window") {
                nextStep.config.condition_window_seconds = Number(values.condition_window_seconds || 0);
            } else {
                delete nextStep.config.condition_window_seconds;
            }
            nextStep.config.entity_linkage_mode = String(values.entity_linkage_mode || "any");
            if (nextStep.config.entity_linkage_mode === "specific") {
                nextStep.config.entity_type = String(values.entity_type || "").trim();
                nextStep.config.entity_id = String(values.entity_id || "").trim();
            } else {
                delete nextStep.config.entity_type;
                delete nextStep.config.entity_id;
            }
        }
        if (stepType === "wait_event") {
            nextStep.config.event_name = String(values.event_name || "");
            nextStep.config.poll_seconds = Number(values.poll_seconds || 30);
            nextStep.config.timeout_seconds = Number(values.timeout_seconds || 0);
            nextStep.config.entity_linkage_mode = String(values.entity_linkage_mode || "any");
            if (nextStep.config.entity_linkage_mode === "specific") {
                nextStep.config.entity_type = String(values.entity_type || "").trim();
                nextStep.config.entity_id = String(values.entity_id || "").trim();
            } else {
                delete nextStep.config.entity_type;
                delete nextStep.config.entity_id;
            }
        }
        if (stepType === "action") {
            const actionType = String(values.action_type || "send_whatsapp_template");
            nextStep.config.action_type = actionType;
            if (actionType === "send_whatsapp_template") {
                nextStep.config.template_id = Number(values.template_id || 0) || null;
                nextStep.config.missing_variable_policy =
                    String(values.missing_variable_policy || "skip_step") === "fail_run"
                        ? "fail_run"
                        : "skip_step";
            } else if (actionType === "webhook") {
                nextStep.config.webhook_url = String(values.webhook_url || "").trim();
                nextStep.config.method = String(values.webhook_method || "POST").toUpperCase();
                const bodyRaw = String(values.webhook_body_json || "").trim();
                if (bodyRaw) {
                    try {
                        nextStep.config.body_json = JSON.parse(bodyRaw);
                    } catch {
                        message.error("Webhook body must be valid JSON");
                        return;
                    }
                } else {
                    nextStep.config.body_json = {};
                }
            } else if (actionType === "tag_client") {
                nextStep.config.op = String(values.tag_op || "add").toLowerCase() === "remove" ? "remove" : "add";
                nextStep.config.tag = String(values.tag_value || "").trim();
            } else if (actionType === "set_property") {
                nextStep.config.property_key = String(values.property_key || "").trim();
                nextStep.config.value_text = String(values.property_value_text || "");
            }
        }
        if (existingStep && String(existingStep.step_type) !== String(stepType)) {
            const currentOutgoing = edges.filter(
                (e) => Number(e.from_step_index) === Number(selectedStepIndex)
            );
            if (currentOutgoing.length) {
                const fromType = String(existingStep.step_type || "");
                const remapped = currentOutgoing.map((e) => ({
                    ...e,
                    edge_key: remapEdgeKeyByTypeChange(
                        fromType,
                        stepType,
                        String(e.edge_key || "default")
                    ),
                }));
                upsertOutgoing(Number(selectedStepIndex), remapped);
            }
        }
        updateStep(Number(selectedStepIndex), nextStep);
        setModalOpen(false);
    };

    const ensurePrimaryTrigger = async (entryEventName: string, silent = false): Promise<number | null> => {
        let triggerId = Number(primaryTriggerId || 0) || null;
        const eventName = String(entryEventName || "").trim();
        if (!triggerId) {
            const created = await CreateV2AutomationTrigger(domain, automationId, {
                title: `${String(name || "").trim() || "Automation"} Trigger`,
                event_name: eventName || "event.name",
                active: 1,
                cooldown_seconds: 0,
                max_active_runs_per_client: 1,
            });
            if (!created.ok) {
                if (!silent) message.error(created.error || "Failed creating automation trigger");
                return null;
            }
            triggerId = Number(created.data?.id || created.data?.triggerId || 0) || null;
            setPrimaryTriggerId(triggerId);
            setPrimaryTriggerEventName(String(created.data?.entry_event_name || eventName || "").trim());
            setEntryConditionId(Number(created.data?.entry_condition_id || 0) || null);
            setExitConditionId(null);
        }

        if (!triggerId) return null;

        if (eventName && eventName !== String(primaryTriggerEventName || "").trim()) {
            const up = await UpdateV2TriggerEntryEvent(domain, triggerId, { event_name: eventName });
            if (!up.ok) {
                if (!silent) message.error(up.error || "Failed syncing trigger entry event");
                return null;
            }
            setPrimaryTriggerEventName(eventName);
        }

        return triggerId;
    };

    const reloadPrimaryTriggerState = async () => {
        if (!automationId) return;
        const res = await GetV2AutomationTriggers(domain, automationId);
        if (!res.ok) return;
        const list = Array.isArray(res.data) ? res.data : [];
        const picked = list[0];
        if (!picked) return;
        setPrimaryTriggerId(Number(picked.id || 0) || null);
        setPrimaryTriggerEventName(String(picked.entry_event_name || "").trim());
        setEntryConditionId(Number(picked.entry_condition_id || 0) || null);
        setExitConditionId(Number(picked.exit_condition_id || 0) || null);
    };

    const reloadEntryTreeBundle = async (conditionId?: number | null) => {
        const cid = Number(conditionId || entryConditionId || 0);
        if (!cid) {
            setEntryTreeBundle(null);
            return;
        }
        const b = await GetConditionTreeBundle(domain, cid);
        if (b.ok) setEntryTreeBundle(b.data || null);
    };

    const cloneEntryBundle = (bundle: TreeBundle | null) =>
        bundle ? (JSON.parse(JSON.stringify(bundle)) as TreeBundle) : null;

    const updateEntryRootOperator = async (operator: "AND" | "OR") => {
        if (!entryEditor?.rootConditionId) return;
        const prev = cloneEntryBundle(entryTreeBundle);
        if (prev) {
            const next = cloneEntryBundle(prev)!;
            if (next.conditionsById?.[String(entryEditor.rootConditionId)]) {
                next.conditionsById[String(entryEditor.rootConditionId)].operator = operator;
            }
            setEntryTreeBundle(next);
        }
        const res = await PatchCondition(domain, entryEditor.rootConditionId, { operator });
        if (!res.ok) {
            if (prev) setEntryTreeBundle(prev);
            message.error(res.error || "Failed updating entry operator");
            return;
        }
        onDraftChanged?.();
    };

    const addEntryGroup = async () => {
        if (!entryEditor?.rootConditionId) return;
        const sortOrder = entryEditor.groups.length;
        const tempConditionId = -(Date.now() % 1000000000);
        const tempItemId = -(Math.floor(Math.random() * 1000000000));
        const prev = cloneEntryBundle(entryTreeBundle);
        if (prev) {
            const next = cloneEntryBundle(prev)!;
            next.conditionsById = next.conditionsById || {};
            next.itemsByConditionId = next.itemsByConditionId || {};
            const rootItems = Array.isArray(next.itemsByConditionId[String(entryEditor.rootConditionId)])
                ? next.itemsByConditionId[String(entryEditor.rootConditionId)]
                : [];
            next.itemsByConditionId[String(entryEditor.rootConditionId)] = [
                ...rootItems,
                {
                    id: tempItemId,
                    condition_id: Number(entryEditor.rootConditionId),
                    item_type: "condition",
                    item_id: tempConditionId,
                    sort_order: sortOrder,
                },
            ];
            next.conditionsById[String(tempConditionId)] = {
                id: tempConditionId,
                title: `Group ${sortOrder + 1}`,
                operator: "AND",
            };
            next.itemsByConditionId[String(tempConditionId)] = [];
            setEntryTreeBundle(next);
        }
        const res = await CreateChildGroup(domain, entryEditor.rootConditionId, {
            operator: "AND",
            title: `Group ${sortOrder + 1}`,
            sort_order: sortOrder,
        });
        if (!res.ok) {
            if (prev) setEntryTreeBundle(prev);
            message.error(res.error || "Failed adding group");
            return null;
        }
        await reloadEntryTreeBundle(entryEditor.rootConditionId);
        onDraftChanged?.();
        return Number(res.data?.childConditionId || 0) || null;
    };

    const removeEntryGroup = async (conditionItemId: number | null) => {
        if (!conditionItemId) return;
        const prev = cloneEntryBundle(entryTreeBundle);
        if (prev) {
            const next = cloneEntryBundle(prev)!;
            const itemsByConditionId = next.itemsByConditionId || {};
            let removedConditionId: number | null = null;
            Object.keys(itemsByConditionId).forEach((k) => {
                const list = Array.isArray(itemsByConditionId[k]) ? itemsByConditionId[k] : [];
                const found = list.find((it: any) => Number(it?.id) === Number(conditionItemId));
                if (found && String(found?.item_type) === "condition") {
                    removedConditionId = Number(found?.item_id || 0) || null;
                }
                itemsByConditionId[k] = list.filter((it: any) => Number(it?.id) !== Number(conditionItemId));
            });
            if (removedConditionId != null) {
                delete next.conditionsById?.[String(removedConditionId)];
                const groupItems = Array.isArray(next.itemsByConditionId?.[String(removedConditionId)])
                    ? next.itemsByConditionId[String(removedConditionId)]
                    : [];
                for (const gi of groupItems) {
                    if (String(gi?.item_type) === "basic_condition") {
                        delete next.basicById?.[String(Number(gi?.item_id || 0))];
                    }
                }
                delete next.itemsByConditionId?.[String(removedConditionId)];
            }
            setEntryTreeBundle(next);
        }
        const res = await DeleteConditionItem(domain, conditionItemId);
        if (!res.ok) {
            if (prev) setEntryTreeBundle(prev);
            message.error(res.error || "Failed removing group");
            return;
        }
        onDraftChanged?.();
    };

    const patchEntryGroupTitle = async (conditionId: number, title: string) => {
        const prev = cloneEntryBundle(entryTreeBundle);
        if (prev) {
            const next = cloneEntryBundle(prev)!;
            if (next.conditionsById?.[String(conditionId)]) {
                next.conditionsById[String(conditionId)].title = title.trim() || null;
            }
            setEntryTreeBundle(next);
        }
        const res = await PatchCondition(domain, conditionId, { title: title.trim() || null });
        if (!res.ok) {
            if (prev) setEntryTreeBundle(prev);
            message.error(res.error || "Failed updating group title");
            return;
        }
        onDraftChanged?.();
    };

    const patchEntryGroupTimeframe = async (conditionId: number, timeframeSeconds: number | null) => {
        const prev = cloneEntryBundle(entryTreeBundle);
        if (prev) {
            const next = cloneEntryBundle(prev)!;
            if (next.conditionsById?.[String(conditionId)]) {
                next.conditionsById[String(conditionId)].timeframe_seconds = timeframeSeconds;
            }
            setEntryTreeBundle(next);
        }
        const res = await PatchCondition(domain, conditionId, { timeframe_seconds: timeframeSeconds });
        if (!res.ok) {
            if (prev) setEntryTreeBundle(prev);
            message.error(res.error || "Failed updating group time scope");
            return;
        }
        onDraftChanged?.();
    };

    const addEntryLeaf = async (conditionId: number) => {
        const tempBasicId = -(Date.now() % 1000000000);
        const tempItemId = -(Math.floor(Math.random() * 1000000000));
        const prev = cloneEntryBundle(entryTreeBundle);
        if (prev) {
            const next = cloneEntryBundle(prev)!;
            next.itemsByConditionId = next.itemsByConditionId || {};
            next.basicById = next.basicById || {};
            const list = Array.isArray(next.itemsByConditionId[String(conditionId)])
                ? next.itemsByConditionId[String(conditionId)]
                : [];
            next.itemsByConditionId[String(conditionId)] = [
                ...list,
                {
                    id: tempItemId,
                    condition_id: conditionId,
                    item_type: "basic_condition",
                    item_id: tempBasicId,
                    sort_order: list.length,
                },
            ];
            next.basicById[String(tempBasicId)] = {
                id: tempBasicId,
                event_name: "event.name",
                aggregation: "exists",
                operator: "EXISTS",
                threshold: null,
            };
            setEntryTreeBundle(next);
        }
        const res = await CreateEventLeaf(domain, conditionId, {
            event_name: "event.name",
            aggregation: "exists",
            operator: "EXISTS",
            threshold: null,
        });
        if (!res.ok) {
            if (prev) setEntryTreeBundle(prev);
            message.error(res.error || "Failed adding condition");
            return;
        }
        // Reload full entry tree from root, not from the edited group,
        // otherwise siblings disappear until a page refresh.
        await reloadEntryTreeBundle();
        onDraftChanged?.();
    };

    const removeEntryLeaf = async (itemId: number) => {
        const prev = cloneEntryBundle(entryTreeBundle);
        if (prev) {
            const next = cloneEntryBundle(prev)!;
            let removedBasicId: number | null = null;
            Object.keys(next.itemsByConditionId || {}).forEach((k) => {
                const list = Array.isArray(next.itemsByConditionId[k]) ? next.itemsByConditionId[k] : [];
                const found = list.find((it: any) => Number(it?.id) === Number(itemId));
                if (found && String(found?.item_type) === "basic_condition") {
                    removedBasicId = Number(found?.item_id || 0) || null;
                }
                next.itemsByConditionId[k] = list.filter((it: any) => Number(it?.id) !== Number(itemId));
            });
            if (removedBasicId != null) {
                delete next.basicById?.[String(removedBasicId)];
            }
            setEntryTreeBundle(next);
        }
        const res = await DeleteConditionItem(domain, itemId);
        if (!res.ok) {
            if (prev) setEntryTreeBundle(prev);
            message.error(res.error || "Failed removing condition");
            return;
        }
        onDraftChanged?.();
    };

    const patchEntryLeaf = async (
        basicId: number,
        patch: Partial<{
            event_name: string | null;
            aggregation: "exists" | "count";
            operator: "EXISTS" | "NOT_EXISTS" | ">=" | ">" | "<=" | "<" | "=" | "!=";
            threshold: number | null;
            filters_json: any | null;
        }>
    ) => {
        const prev = cloneEntryBundle(entryTreeBundle);
        if (prev) {
            const next = cloneEntryBundle(prev)!;
            if (next.basicById?.[String(basicId)]) {
                next.basicById[String(basicId)] = {
                    ...next.basicById[String(basicId)],
                    ...patch,
                };
            }
            setEntryTreeBundle(next);
        }
        const res = await PatchBasicCondition(domain, basicId, patch);
        if (!res.ok) {
            if (prev) setEntryTreeBundle(prev);
            message.error(res.error || "Failed updating condition");
            return;
        }
        onDraftChanged?.();
    };

    const openExitConditionEditor = async () => {
        if (!primaryTriggerId) {
            message.error("Trigger is missing");
            return;
        }
        if (exitConditionId) {
            setExitConditionModalOpen(true);
            return;
        }
        const ensured = await EnsureTriggerExitCondition(domain, primaryTriggerId);
        if (!ensured.ok || !ensured.data?.exit_condition_id) {
            message.error(ensured.error || "Failed creating exit condition");
            return;
        }
        setExitConditionId(Number(ensured.data.exit_condition_id));
        setExitConditionModalOpen(true);
    };

    const clearExitCondition = async () => {
        if (!primaryTriggerId) return;
        const res = await UpdateV2Trigger(domain, primaryTriggerId, { exit_condition_id: null });
        if (!res.ok) {
            message.error(res.error || "Failed clearing exit condition");
            return;
        }
        setExitConditionId(null);
        showToast({ skin: ToastSkin.approval, text: "Exit condition cleared" });
    };

    const saveTriggerSettings = async () => {
        if (!primaryTriggerId) {
            message.error("Missing trigger");
            return;
        }
        setSaving(true);
        try {
            const patchRes = await UpdateV2Trigger(domain, primaryTriggerId, {
                title: String(primaryTriggerTitle || "").trim() || "Trigger",
                cooldown_seconds: Math.max(0, Number(primaryTriggerCooldownSeconds || 0)),
                max_active_runs_per_client: Math.max(1, Number(primaryTriggerMaxActiveRuns || 1)),
                active: primaryTriggerActive ? 1 : 0,
            });
            if (!patchRes.ok) {
                message.error(patchRes.error || "Failed saving trigger settings");
                return;
            }
            showToast({ skin: ToastSkin.approval, text: "Trigger settings saved" });
        } finally {
            setSaving(false);
        }
    };

    const buildSavePayload = () => {
        const trimmedName = String(name || "").trim();
        if (!trimmedName || !steps.length) return null;

        const stepsPayload = [...steps]
            .map((s) => {
                const offset = nodeOffsets[Number(s.step_index)];
                if (!offset) return s;
                return {
                    ...s,
                    config: {
                        ...(s.config || {}),
                        ui_position: {
                            x: Math.round(Number(offset.x || 0)),
                            y: Math.round(Number(offset.y || 0)),
                        },
                    },
                };
            })
            .sort((a, b) => Number(a.step_index) - Number(b.step_index));
        const stepSet = new Set(stepsPayload.map((s) => Number(s.step_index)));
        const edgesPayload = edges
            .filter((e) => stepSet.has(Number(e.from_step_index)) && stepSet.has(Number(e.to_step_index)))
            .map((e, i) => ({
                from_step_index: Number(e.from_step_index),
                to_step_index: Number(e.to_step_index),
                edge_key: String(e.edge_key || "").trim(),
                sort_order: Number(e.sort_order ?? i),
            }))
            .sort((a, b) =>
                Number(a.from_step_index) - Number(b.from_step_index) ||
                Number(a.sort_order) - Number(b.sort_order) ||
                String(a.edge_key).localeCompare(String(b.edge_key)) ||
                Number(a.to_step_index) - Number(b.to_step_index)
            );

        const automationPayload = {
            name: trimmedName,
            description: description?.trim() || null,
            active: (active ? 1 : 0) as 0 | 1,
        };
        const snapshot = JSON.stringify({
            automation: automationPayload,
            steps: stepsPayload,
            edges: edgesPayload,
        });

        return { trimmedName, stepsPayload, edgesPayload, automationPayload, snapshot };
    };

    const persistTree = async (opts?: {
        silent?: boolean;
        strict?: boolean;
        publish?: boolean;
        autosaveGeneration?: number;
    }) => {
        const silent = Boolean(opts?.silent);
        const strict = opts?.strict !== false;
        const publish = Boolean(opts?.publish);
        const autosaveGeneration = Number(opts?.autosaveGeneration || 0);
        const isAutosave = autosaveGeneration > 0;
        const isStaleAutosave = () =>
            isAutosave && autosaveGeneration !== Number(autosaveGenerationRef.current || 0);
        const returnIfStaleAutosave = () => {
            if (!isStaleAutosave()) return false;
            requestImmediateAutosave();
            return true;
        };
        const payload = buildSavePayload();
        if (!payload) {
            if (strict) {
                if (!String(name || "").trim()) message.error("Automation name is required");
                else message.error("Add at least one node");
            }
            return false;
        }

        // Allow explicit publish even when draft snapshot did not change.
        if (!publish && payload.snapshot === lastSavedSnapshotRef.current) return true;

        if (!entryConditionId) {
            if (!silent) message.error("Entry condition is missing");
            return false;
        }

        const validationErrors = runtimeValidationErrors(payload.stepsPayload, payload.edgesPayload);
        if (validationErrors.length) {
            setSaveValidationErrors(validationErrors);
            if (!silent) message.error("Automation failed validation and was not saved.");
            return false;
        }
        if (saveValidationErrors.length) setSaveValidationErrors([]);

        setSaving(true);
        try {
            if (returnIfStaleAutosave()) return false;
            const triggerSyncOk = await ensurePrimaryTrigger(firstWaitEventName(payload.stepsPayload), silent);
            if (!triggerSyncOk) return false;
            if (returnIfStaleAutosave()) return false;

            const upRes = await UpdateV2Automation(domain, automationId, payload.automationPayload);
            if (!upRes.ok) {
                if (!silent) message.error(upRes.error || "Failed saving automation details");
                return false;
            }
            if (returnIfStaleAutosave()) return false;

            const graphRes = await ReplaceV2AutomationSteps(
                domain,
                automationId,
                payload.stepsPayload,
                payload.edgesPayload,
                { publish }
            );
            if (!graphRes.ok) {
                if (!silent) message.error(graphRes.error || "Failed saving graph");
                return false;
            }
            if (returnIfStaleAutosave()) return false;

            lastSavedSnapshotRef.current = payload.snapshot;
            if (saveValidationErrors.length) setSaveValidationErrors([]);
            if (!silent) showToast({ skin: ToastSkin.approval, text: publish ? "Tree published" : "Tree saved" });
            return true;
        } finally {
            setSaving(false);
        }
    };

    const saveAll = async () => {
        await persistTree({ silent: false, strict: true });
    };

    const requestImmediateAutosave = () => {
        autosaveGenerationRef.current += 1;
        const generation = autosaveGenerationRef.current;
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        window.setTimeout(() => {
            persistTree({ silent: true, strict: false, autosaveGeneration: generation }).catch(() => null);
        }, 0);
    };

    const makeHistorySnapshot = (): EditorHistorySnapshot => ({
        name,
        description,
        active,
        steps: JSON.parse(JSON.stringify(steps || [])),
        edges: JSON.parse(JSON.stringify(edges || [])),
        nodeOffsets: JSON.parse(JSON.stringify(nodeOffsets || {})),
    });

    const applyHistorySnapshot = (snap: EditorHistorySnapshot) => {
        historyApplyingRef.current = true;
        setName(String(snap.name || ""));
        setDescription(String(snap.description || ""));
        setActive(Boolean(snap.active));
        setSteps(JSON.parse(JSON.stringify(snap.steps || [])));
        setEdges(JSON.parse(JSON.stringify(snap.edges || [])));
        setNodeOffsets(JSON.parse(JSON.stringify(snap.nodeOffsets || {})));
        window.setTimeout(() => {
            historyApplyingRef.current = false;
        }, 0);
    };

    const undoChange = () => {
        if (!undoStack.length) return;
        autosaveGenerationRef.current += 1;
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        // Force a fresh autosave for the post-undo state, even if it matches a stale local snapshot marker.
        lastSavedSnapshotRef.current = "";
        forceImmediateSaveRef.current = true;
        const current = makeHistorySnapshot();
        const previous = undoStack[undoStack.length - 1];
        setUndoStack((stack) => stack.slice(0, -1));
        setRedoStack((stack) => {
            const next = [...stack, current];
            return next.length > 10 ? next.slice(next.length - 10) : next;
        });
        applyHistorySnapshot(previous);
    };

    const redoChange = () => {
        if (!redoStack.length) return;
        autosaveGenerationRef.current += 1;
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        // Force a fresh autosave for the post-redo state, even if it matches a stale local snapshot marker.
        lastSavedSnapshotRef.current = "";
        forceImmediateSaveRef.current = true;
        const current = makeHistorySnapshot();
        const nextSnapshot = redoStack[redoStack.length - 1];
        setRedoStack((stack) => stack.slice(0, -1));
        setUndoStack((stack) => {
            const next = [...stack, current];
            return next.length > 10 ? next.slice(next.length - 10) : next;
        });
        applyHistorySnapshot(nextSnapshot);
    };

    const publishAll = async () => {
        await persistTree({ silent: false, strict: true, publish: true });
    };

    useEffect(() => {
        if (loading) return;
        autosaveGenerationRef.current += 1;
        const generation = autosaveGenerationRef.current;
        const shouldForceImmediate = forceImmediateSaveRef.current;
        const payload = buildSavePayload();
        if (!payload) return;
        if (!lastSavedSnapshotRef.current && !shouldForceImmediate) {
            lastSavedSnapshotRef.current = payload.snapshot;
            return;
        }
        if (payload.snapshot === lastSavedSnapshotRef.current) return;

        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        if (shouldForceImmediate) {
            forceImmediateSaveRef.current = false;
            persistTree({ silent: true, strict: false, autosaveGeneration: generation }).catch(() => null);
            return;
        }
        autosaveTimerRef.current = window.setTimeout(() => {
            persistTree({ silent: true, strict: false, autosaveGeneration: generation }).catch(() => null);
        }, 1400);
    }, [loading, name, description, active, steps, edges, nodeOffsets]);

    useEffect(() => {
        if (loading) return;
        if (draggingStepId !== null) return;
        const snapshot = JSON.stringify({
            name,
            description,
            active,
            steps,
            edges,
            nodeOffsets,
        });

        if (!historyInitializedRef.current) {
            historyInitializedRef.current = true;
            lastHistorySnapshotRef.current = snapshot;
            return;
        }

        if (snapshot === lastHistorySnapshotRef.current) return;
        const prevRaw = lastHistorySnapshotRef.current;
        lastHistorySnapshotRef.current = snapshot;

        if (historyApplyingRef.current) return;

        try {
            const parsed = JSON.parse(prevRaw || "{}");
            const previous: EditorHistorySnapshot = {
                name: String(parsed?.name || ""),
                description: String(parsed?.description || ""),
                active: Boolean(parsed?.active),
                steps: Array.isArray(parsed?.steps) ? parsed.steps : [],
                edges: Array.isArray(parsed?.edges) ? parsed.edges : [],
                nodeOffsets: parsed?.nodeOffsets && typeof parsed.nodeOffsets === "object" ? parsed.nodeOffsets : {},
            };
            setUndoStack((stack) => {
                const next = [...stack, previous];
                return next.length > 10 ? next.slice(next.length - 10) : next;
            });
            setRedoStack([]);
        } catch {
            // no-op
        }
    }, [loading, draggingStepId, name, description, active, steps, edges, nodeOffsets]);

    useEffect(() => {
        return () => {
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!saveValidationErrors.length) return;
        const errs = runtimeValidationErrors(steps, edges);
        if (!errs.length) setSaveValidationErrors([]);
    }, [steps, edges, saveValidationErrors.length]);

    useEffect(() => {
        onFullscreenChange?.(isFullscreen);
    }, [isFullscreen, onFullscreenChange]);

    useEffect(() => {
        if (!editable || !openExitConditionSignal) return;
        openExitConditionEditor();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openExitConditionSignal]);

    if (loading || loadingTrigger) {
        return (
            <div className="v2at-loading">
                <Loader size={72} />
            </div>
        );
    }

    return (
        <div className="v2at-root" style={compactCanvasShell ? { height: "100%", gap: 0 } : undefined}>
            <Card
                bordered={compactCanvasShell ? false : editable}
                bodyStyle={compactCanvasShell ? { padding: 0 } : (!editable ? { padding: 0 } : undefined)}
                style={compactCanvasShell ? { border: "none", boxShadow: "none", background: "transparent" } : (!editable ? { border: "none", boxShadow: "none", background: "transparent" } : undefined)}
            >
                {showHeaderSection ? (
                    <>
                        <Space style={{ width: "100%", justifyContent: "space-between" }}>
                            <div>
                                <Title level={4} style={{ margin: 0 }}>Automation Tree Editor</Title>
                                <Text type="secondary">Visual decisions flow. Click any node to edit in a helper modal.</Text>
                            </div>
                        </Space>

                        <Divider />

                        <Space wrap style={{ marginBottom: 12 }}>
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Automation name" style={{ width: 280 }} />
                            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" style={{ width: 360 }} />
                            <Select
                                value={active ? 1 : 0}
                                onChange={(v) => setActive(Boolean(v))}
                                options={[
                                    { value: 1, label: "Active" },
                                    { value: 0, label: "Inactive" },
                                ]}
                                style={{ width: 140 }}
                            />
                        </Space>
                        <div style={{ marginBottom: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                            <Input
                                value={primaryTriggerTitle}
                                onChange={(e) => setPrimaryTriggerTitle(e.target.value)}
                                placeholder="Trigger title"
                                style={{ width: 220 }}
                            />
                            <InputNumber
                                min={0}
                                value={primaryTriggerCooldownSeconds}
                                onChange={(v) => setPrimaryTriggerCooldownSeconds(Number(v || 0))}
                                addonBefore="Cooldown (sec)"
                            />
                            <InputNumber
                                min={1}
                                value={primaryTriggerMaxActiveRuns}
                                onChange={(v) => setPrimaryTriggerMaxActiveRuns(Math.max(1, Number(v || 1)))}
                                addonBefore="Max active / client"
                            />
                            <Select
                                value={primaryTriggerActive ? 1 : 0}
                                onChange={(v) => setPrimaryTriggerActive(Number(v) === 1)}
                                options={[
                                    { value: 1, label: "Trigger active" },
                                    { value: 0, label: "Trigger inactive" },
                                ]}
                                style={{ width: 150 }}
                            />
                            <Button onClick={saveTriggerSettings} loading={saving}>Save trigger settings</Button>
                        </div>
                        <div style={{ marginBottom: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                            <V2Tag color={entryConditionId ? "blue" : "default"}>
                                Entry condition: {entryConditionId ? "configured" : "missing"}
                            </V2Tag>
                            <V2Tag color={exitConditionId ? "purple" : "default"}>
                                Exit condition: {exitConditionId ? "configured" : "not set"}
                            </V2Tag>
                            <Button size="small" onClick={openExitConditionEditor}>
                                {exitConditionId ? "Edit exit condition" : "Add exit condition"}
                            </Button>
                            {exitConditionId ? (
                                <Button size="small" danger onClick={clearExitCondition}>
                                    Clear exit condition
                                </Button>
                            ) : null}
                        </div>
                    </>
                ) : null}
                <AutomationTreeCanvas
                    width={graph.width}
                    height={graph.height}
                    zoom={zoom}
                    editable={editable}
                    isFullscreen={isFullscreen}
                    canvasRef={boardCanvasRef}
                    stageShellStyle={
                        stageHeight
                            ? { height: stageHeight, maxHeight: stageHeight }
                            : compactCanvasShell
                                ? { height: "100%", maxHeight: "100%" }
                                : undefined
                    }
                    validationBanner={
                        editable && saveValidationErrors.length ? (
                            <div className="v2at-board-validation">
                                <ExclamationCircleOutlined />
                                <span>Validation failed. Auto-save paused.</span>
                                <Button
                                    size="small"
                                    type="text"
                                    icon={<ExclamationCircleOutlined />}
                                    onClick={() => setValidationDetailsOpen(true)}
                                />
                            </div>
                        ) : null
                    }
                    actionBar={
                        editable ? (
                        <>
                            <Tooltip title="Zoom out">
                                <Button
                                    size="small"
                                    icon={<ZoomOutOutlined />}
                                    onClick={() => setZoom((z) => Math.max(0.6, Number((z - 0.1).toFixed(2))))}
                                />
                            </Tooltip>
                            <Text className="v2at-board-zoom-indicator">{Math.round(zoom * 100)}%</Text>
                            <Tooltip title="Zoom in">
                                <Button
                                    size="small"
                                    icon={<ZoomInOutlined />}
                                    onClick={() => setZoom((z) => Math.min(1.8, Number((z + 0.1).toFixed(2))))}
                                />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                                <Button
                                    size="small"
                                    icon={isFullscreen ? <ShrinkOutlined /> : <ExpandOutlined />}
                                    onClick={() => setIsFullscreen((v) => !v)}
                                />
                            </Tooltip>
                            <Tooltip title={steps.length ? "Add child node" : "Add first node"}>
                                <Button
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={addStep}
                                    disabled={steps.length > 0 && (selectedStepIndex == null || Number(selectedStepIndex) === -1)}
                                />
                            </Tooltip>
                            <Tooltip title="Save tree">
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    loading={saving}
                                    onClick={saveAll}
                                />
                            </Tooltip>
                            <Tooltip title="Undo">
                                <Button
                                    size="small"
                                    icon={<UndoOutlined />}
                                    onClick={undoChange}
                                    disabled={!undoStack.length}
                                />
                            </Tooltip>
                            <Tooltip title="Redo">
                                <Button
                                    size="small"
                                    icon={<RedoOutlined />}
                                    onClick={redoChange}
                                    disabled={!redoStack.length}
                                />
                            </Tooltip>
                            {showPublishButton ? (
                                <Tooltip title="Publish tree">
                                    <Button
                                        size="small"
                                        icon={<UploadOutlined />}
                                        loading={saving}
                                        onClick={confirmPublishTree}
                                    >
                                        Publish
                                    </Button>
                                </Tooltip>
                            ) : null}
                        </>
                        ) : null
                    }

                >

                        {!editable
                            ? [
                                <div
                                    key="ro-hit-entry"
                                    data-step-id={-1}
                                    className="v2at-node-hitbox"
                                    style={{
                                        left: graph.entryNode.x,
                                        top: graph.entryNode.y,
                                        width: graph.entryNode.width,
                                        height: graph.entryNode.height,
                                    }}
                                    onMouseEnter={() => setHoveredNodeId(-1)}
                                    onMouseLeave={() => setHoveredNodeId((prev) => (prev === -1 ? null : prev))}
                                />,
                                ...graph.nodes.map((n) => (
                                    (() => {
                                        const hitbox = (
                                            <div
                                                key={`ro-hit-${n.id}`}
                                                data-step-id={n.id}
                                                className="v2at-node-hitbox"
                                                style={{ left: n.x, top: n.y, width: n.width, height: n.height }}
                                                onMouseEnter={() => setHoveredNodeId(n.id)}
                                                onMouseLeave={() => setHoveredNodeId((prev) => (prev === n.id ? null : prev))}
                                            />
                                        );
                                        if (n.step.step_type !== "condition") return hitbox;
                                        return (
                                            <Tooltip key={`ro-tip-${n.id}`} title={conditionSummaryText(n.step.config || {})} mouseEnterDelay={0.2}>
                                                {hitbox}
                                            </Tooltip>
                                        );
                                    })()
                                )),
                            ]
                            : null}

                        {editable ? (
                        <>
                        <div
                            data-step-id={-1}
                            className="v2at-node-hitbox"
                            style={{
                                left: graph.entryNode.x,
                                top: graph.entryNode.y,
                                width: graph.entryNode.width,
                                height: graph.entryNode.height,
                            }}
                            onMouseEnter={() => setHoveredNodeId(-1)}
                            onMouseLeave={() => setHoveredNodeId((prev) => (prev === -1 ? null : prev))}
                            onClick={() => {
                                if (Number(selectedStepIndex) !== -1) setSelectedStepIndex(-1);
                            }}
                        >
                            {hasMultipleEntryGroups ? (
                                <div
                                    className={`v2at-node-menu-overlay v2at-entry-mode-overlay ${
                                        hoveredNodeId === -1 || hoveredEntryGroupId != null ? "is-visible" : ""
                                    }`}
                                    style={{ left: 65, top: 16, right: "auto" }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Select
                                        size="small"
                                        className="v2at-entry-operator-select"
                                        popupClassName="v2at-entry-operator-dropdown"
                                        value={entryEditor?.rootOperator || "AND"}
                                        options={[
                                            { value: "AND", label: "AND" },
                                            { value: "OR", label: "OR" },
                                        ]}
                                        onChange={(v) => {
                                            updateEntryRootOperator((String(v) === "OR" ? "OR" : "AND") as "AND" | "OR");
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>

                        {graph.entryGroupHitboxes.map((hb) => (
                            <div
                                key={`entry-g-${hb.conditionId}`}
                                className="v2at-suggestion-hitbox"
                                style={{ left: hb.x, top: hb.y, width: hb.width, height: hb.height }}
                                onMouseEnter={() => setHoveredEntryGroupId(Number(hb.conditionId))}
                                onMouseLeave={() =>
                                    setHoveredEntryGroupId((prev) =>
                                        Number(prev) === Number(hb.conditionId) ? null : prev
                                    )
                                }
                                onClick={() => {
                                    setSelectedStepIndex(-1);
                                    setEditingEntryGroupId(Number(hb.conditionId));
                                    setEntryGroupModalOpen(true);
                                }}
                            />
                        ))}

                        <div
                            className="v2at-suggestion-hitbox"
                            style={{
                                left: graph.entrySuggestionHitbox.x,
                                top: graph.entrySuggestionHitbox.y,
                                width: graph.entrySuggestionHitbox.width,
                                height: graph.entrySuggestionHitbox.height,
                            }}
                            onMouseEnter={() => setHoveredSuggestionId("entry-add-group")}
                            onMouseLeave={() =>
                                setHoveredSuggestionId((prev) => (prev === "entry-add-group" ? null : prev))
                            }
                            onClick={async () => {
                                const newId = await addEntryGroup();
                                if (!newId) return;
                                setEditingEntryGroupId(newId);
                                setEntryGroupModalOpen(true);
                            }}
                        />

                        {graph.nodes.map((n) => {
                            const hitbox = (
                                <div
                                    key={`hit-${n.id}`}
                                    data-step-id={n.id}
                                    className={`v2at-node-hitbox ${draggingStepId === n.id ? "dragging" : ""}`}
                                    style={{ left: n.x, top: n.y, width: n.width, height: n.height }}
                                    onMouseEnter={() => setHoveredNodeId(n.id)}
                                    onMouseLeave={() => setHoveredNodeId((prev) => (prev === n.id ? null : prev))}
                                    onClick={() => {
                                        if (suppressClickForStepId === n.id) return;
                                        selectOrOpenNode(n.id);
                                    }}
                                >
                                    {showStepNumber ? <V2Tag className="v2at-step-tag">Step #{n.id}</V2Tag> : null}
                                    <div className={`v2at-node-menu-overlay ${hoveredNodeId === n.id || openNodeMenuId === n.id ? "is-visible" : ""}`} onClick={(e) => e.stopPropagation()}>
                                        <Dropdown
                                            trigger={["click"]}
                                            placement="bottomRight"
                                            open={openNodeMenuId === n.id}
                                            onOpenChange={(open) => setOpenNodeMenuId(open ? n.id : null)}
                                            getPopupContainer={() => document.body}
                                            overlayClassName="v2at-node-menu-dropdown"
                                            overlayStyle={{ zIndex: 20050 }}
                                            menu={{
                                                items: [
                                                    { key: "edit", label: "Edit", icon: <EditOutlined /> },
                                                    { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true },
                                                ],
                                                onClick: ({ key }) => {
                                                    if (key === "edit") openNodeModal(n.id);
                                                    if (key === "delete") confirmDeleteStep(n.id);
                                                },
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                type="text"
                                                className="v2at-node-menu-btn"
                                                icon={<EllipsisOutlined />}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </Dropdown>
                                    </div>
                                </div>
                            );
                            if (n.step.step_type !== "condition") return hitbox;
                            return (
                                <Tooltip key={`tip-${n.id}`} title={conditionSummaryText(n.step.config || {})} mouseEnterDelay={0.2}>
                                    {hitbox}
                                </Tooltip>
                            );
                        })}

                        {graph.suggestions.map((s) => (
                            <div
                                key={`sg-hit-${s.id}`}
                                className="v2at-suggestion-hitbox"
                                style={{ left: s.x, top: s.y, width: graph.nodeWidth, height: graph.nodeHeight - 2 }}
                                onMouseEnter={() => setHoveredSuggestionId(s.id)}
                                onMouseLeave={() =>
                                    setHoveredSuggestionId((prev) => (prev === s.id ? null : prev))
                                }
                                onClick={() => addChildFromSelected(s.edgeKey, { x: s.x, y: s.y })}
                            />
                        ))}

                        {graph.editableEdges
                            .filter((e) => e.editable)
                            .map((e) => (
                                <div
                                    key={e.id}
                                    className="v2at-edge-edit-hitbox"
                                    style={{ left: e.x - 36, top: e.y - 11, width: 72, height: 22 }}
                                    onClick={() => {
                                        setEdgeEditTarget(e);
                                        setEdgeEditKey(String(e.edgeKey || "default"));
                                        setEdgeEditOpen(true);
                                    }}
                                />
                            ))}
                        {graph.insertableEdges.map((e) => (
                            <div
                                key={e.id}
                                className={`v2at-edge-insert-hitbox ${hoveredInsertEdgeId === e.id ? "is-hovered" : ""}`}
                                style={{ left: e.x - 56, top: e.y - 18, width: 112, height: 36 }}
                                onMouseEnter={() => setHoveredInsertEdgeId(e.id)}
                                onMouseLeave={() => setHoveredInsertEdgeId((prev) => (prev === e.id ? null : prev))}
                                onClick={(ev) => ev.stopPropagation()}
                            >
                                <Button
                                    size="small"
                                    shape="circle"
                                    className="v2at-edge-insert-btn"
                                    icon={<PlusOutlined />}
                                    onClick={() => insertNodeBetween(e)}
                                />
                            </div>
                        ))}
                        </>
                        ) : null}
                </AutomationTreeCanvas>
            </Card>

            <Modal
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={saveNodeModal}
                okText="Apply"
                title={selectedStep ? `Step ${selectedStep.step_index}` : "Step"}
                width={720}
                className="v2at-node-modal"
            >
                {selectedStep ? (
                    <>
                        <Form form={modalForm} layout="vertical">
                            <Form.Item name="step_type" hidden>
                                <Input />
                            </Form.Item>
                            <div className="v2at-type-picker">
                                {STEP_TYPE_OPTIONS.map((opt) => {
                                    const active = modalStepType === opt.value;
                                    const typeClass = `type-${String(opt.value).replace("_", "-")}`;
                                    return (
                                        <Button
                                            key={opt.value}
                                            className={`v2at-type-btn ${typeClass} ${active ? "active" : ""}`}
                                            onClick={() => modalForm.setFieldValue("step_type", opt.value)}
                                        >
                                            <div className="v2at-type-btn-content">
                                                <div className="v2at-type-btn-icon">{stepTypeIcon(String(opt.value))}</div>
                                                <div className="v2at-type-btn-title">{opt.label}</div>
                                                <div className="v2at-type-btn-subtitle">{opt.subtitle}</div>
                                            </div>
                                        </Button>
                                    );
                                })}
                            </div>
                            <Form.Item noStyle shouldUpdate>
                                {() => {
                                    const t = (modalForm.getFieldValue("step_type") || "action") as StepType;
                                    if (t === "wait") {
                                        return (
                                            <Text type="secondary">
                                                Wait pauses for a fixed time, then continues via the `default` edge.
                                            </Text>
                                        );
                                    }
                                    if (t === "wait_event") {
                                        return (
                                            <Text type="secondary">
                                                Wait Event polls for a tracked event. It branches via `matched` (event happened) or `timeout` (didn't happen in time).
                                            </Text>
                                        );
                                    }
                                    if (t === "condition") {
                                        return (
                                            <Text type="secondary">
                                                Condition evaluates a condition tree and branches by `true` / `false`.
                                            </Text>
                                        );
                                    }
                                    return (
                                        <Text type="secondary">
                                            Action executes an operation (currently: Send WhatsApp Template).
                                        </Text>
                                    );
                                }}
                            </Form.Item>
                            <div style={{ height: 10 }} />

                            <Form.Item noStyle shouldUpdate>
                                {() => {
                                    const t = (modalForm.getFieldValue("step_type") || "action") as StepType;
                                    if (t === "wait") {
                                        return (
                                            <Form.Item name="delay_seconds" label="Delay seconds">
                                                <InputNumber min={0} style={{ width: 240 }} />
                                            </Form.Item>
                                        );
                                    }
                                    if (t === "condition") {
                                        return (
                                            <Space direction="vertical" style={{ width: "100%" }} size={10}>
                                                <Text strong>Condition builder</Text>
                                                <Text type="secondary">Build grouped logic so OR conditions are explicit and clear.</Text>
                                                <Space align="center">
                                                    <Text type="secondary">Group mode:</Text>
                                                    <Select
                                                        style={{ width: 220 }}
                                                        value={conditionGroupMode}
                                                        options={[
                                                            { value: "ALL_GROUPS", label: "Match ALL groups (AND)" },
                                                            { value: "ANY_GROUP", label: "Match ANY group (OR)" },
                                                        ]}
                                                        onChange={(v) => setConditionGroupMode(v as ConditionGroupMode)}
                                                    />
                                                </Space>
                                                <Space align="center">
                                                    <Text type="secondary">Time scope:</Text>
                                                    <Form.Item name="condition_scope_mode" style={{ marginBottom: 0 }}>
                                                        <Select
                                                            style={{ width: 260 }}
                                                            options={[
                                                                { value: "since_run_start", label: "Since trigger started" },
                                                                { value: "rolling_window", label: "Rolling window" },
                                                                { value: "all_history", label: "All client history" },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item noStyle shouldUpdate>
                                                        {() => {
                                                            const mode = String(modalForm.getFieldValue("condition_scope_mode") || "since_run_start");
                                                            if (mode !== "rolling_window") return null;
                                                            return (
                                                                <Form.Item name="condition_window_seconds" style={{ marginBottom: 0 }}>
                                                                    <InputNumber min={1} addonAfter="sec" />
                                                                </Form.Item>
                                                            );
                                                        }}
                                                    </Form.Item>
                                                </Space>
                                                <Space align="center">
                                                    <Text type="secondary">Entity scope:</Text>
                                                    <Form.Item name="entity_linkage_mode" style={{ marginBottom: 0 }}>
                                                        <Select
                                                            style={{ width: 260 }}
                                                            options={[
                                                                { value: "any", label: "Any entity" },
                                                                { value: "run_context", label: "Same as trigger entity" },
                                                                { value: "specific", label: "Specific entity" },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item noStyle shouldUpdate>
                                                        {() => {
                                                            if (String(modalForm.getFieldValue("entity_linkage_mode") || "any") !== "specific")
                                                                return null;
                                                            return (
                                                                <Space>
                                                                    <Form.Item name="entity_type" style={{ marginBottom: 0 }}>
                                                                        <Input placeholder="entity_type" />
                                                                    </Form.Item>
                                                                    <Form.Item name="entity_id" style={{ marginBottom: 0 }}>
                                                                        <Input placeholder="entity_id" />
                                                                    </Form.Item>
                                                                </Space>
                                                            );
                                                        }}
                                                    </Form.Item>
                                                </Space>
                                                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                                    {conditionGroups.map((group, gIdx) => (
                                                        <div
                                                            key={group.id}
                                                            style={{
                                                                border: "1px solid #dfe7f8",
                                                                borderRadius: 10,
                                                                padding: 10,
                                                                background: "#fbfcff",
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                gap: 8,
                                                            }}
                                                        >
                                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                <Text strong>{gIdx === 0 ? "IF" : `Group ${gIdx + 1}`}</Text>
                                                                <Button danger onClick={() => removeConditionGroup(group.id)} disabled={conditionGroups.length <= 1}>
                                                                    Remove group
                                                                </Button>
                                                            </div>
                                                            {group.rules.map((row, idx) => (
                                                                (() => {
                                                                    const op = String(row.operator || "");
                                                                    const showCountInput =
                                                                        op.startsWith("count_") ||
                                                                        (String(row.field || "") === "event_name" &&
                                                                            ["gte", "gt", "equals", "lte", "lt"].includes(op));
                                                                    return (
                                                                <div
                                                                    key={row.id}
                                                                    style={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: showCountInput
                                                                            ? "70px 1.2fr 1fr 1.2fr 110px 40px"
                                                                            : "70px 1.2fr 1fr 1.2fr 40px",
                                                                        gap: 8,
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Select value={idx === 0 ? "IF" : "AND"} disabled options={idx === 0 ? [{ value: "IF", label: "IF" }] : [{ value: "AND", label: "AND" }]} />
                                                                    <Select
                                                                        showSearch
                                                                        optionFilterProp="label"
                                                                        placeholder="Field"
                                                                        value={row.field || undefined}
                                                                        options={CONDITION_FIELD_OPTIONS}
                                                                        onChange={(v) => updateConditionRule(group.id, row.id, { field: String(v || "") })}
                                                                    />
                                                                    <Select
                                                                        placeholder="Operator"
                                                                        value={row.operator || undefined}
                                                                        options={CONDITION_OPERATOR_OPTIONS}
                                                                        onChange={(v) => updateConditionRule(group.id, row.id, { operator: String(v || "equals") })}
                                                                    />
                                                                    <Input
                                                                        placeholder={String(row.operator || "").startsWith("count_") ? "Event name" : "Value"}
                                                                        value={row.value}
                                                                        onChange={(e) => updateConditionRule(group.id, row.id, { value: e.target.value })}
                                                                    />
                                                                    {showCountInput ? (
                                                                        <InputNumber
                                                                            min={0}
                                                                            placeholder="Count"
                                                                            value={
                                                                                row.count_threshold == null
                                                                                    ? undefined
                                                                                    : Number(row.count_threshold)
                                                                            }
                                                                            onChange={(v) =>
                                                                                updateConditionRule(group.id, row.id, {
                                                                                    count_threshold:
                                                                                        v == null ? null : Number(v),
                                                                                })
                                                                            }
                                                                        />
                                                                    ) : null}
                                                                    <Button danger onClick={() => removeConditionRule(group.id, row.id)} disabled={group.rules.length <= 1}>
                                                                        -
                                                                    </Button>
                                                                </div>
                                                                    );
                                                                })()
                                                            ))}
                                                            <Space>
                                                                <Button onClick={() => addConditionRule(group.id)}>+ Add AND rule</Button>
                                                            </Space>
                                                        </div>
                                                    ))}
                                                </div>
                                                <Space>
                                                    <Button onClick={addConditionGroup}>+ Add group</Button>
                                                </Space>
                                                <div style={{ background: "#f6f8fc", border: "1px solid #dfe7f8", borderRadius: 8, padding: "8px 10px" }}>
                                                    <Text type="secondary">{conditionSummary}</Text>
                                                </div>
                                            </Space>
                                        );
                                    }
                                    if (t === "wait_event") {
                                        return (
                                            <Space direction="vertical" style={{ width: "100%" }}>
                                                <Form.Item name="event_name" label="Event name">
                                                    <Input placeholder="e.g. button.click" />
                                                </Form.Item>
                                                <Space>
                                                    <Form.Item name="poll_seconds" label="Poll seconds">
                                                        <InputNumber min={5} />
                                                    </Form.Item>
                                                    <Form.Item name="timeout_seconds" label="Timeout seconds">
                                                        <InputNumber min={0} />
                                                    </Form.Item>
                                                </Space>
                                                <Space align="center">
                                                    <Text type="secondary">Entity scope:</Text>
                                                    <Form.Item name="entity_linkage_mode" style={{ marginBottom: 0 }}>
                                                        <Select
                                                            style={{ width: 260 }}
                                                            options={[
                                                                { value: "any", label: "Any entity" },
                                                                { value: "run_context", label: "Same as trigger entity" },
                                                                { value: "specific", label: "Specific entity" },
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item noStyle shouldUpdate>
                                                        {() => {
                                                            if (String(modalForm.getFieldValue("entity_linkage_mode") || "any") !== "specific")
                                                                return null;
                                                            return (
                                                                <Space>
                                                                    <Form.Item name="entity_type" style={{ marginBottom: 0 }}>
                                                                        <Input placeholder="entity_type" />
                                                                    </Form.Item>
                                                                    <Form.Item name="entity_id" style={{ marginBottom: 0 }}>
                                                                        <Input placeholder="entity_id" />
                                                                    </Form.Item>
                                                                </Space>
                                                            );
                                                        }}
                                                    </Form.Item>
                                                </Space>
                                            </Space>
                                        );
                                    }
                                    if (t === "action") {
                                        return (
                                            <Space direction="vertical" style={{ width: "100%" }}>
                                                <Form.Item name="action_type" label="Action type">
                                                    <Select
                                                        options={[
                                                            { value: "send_whatsapp_template", label: "Send WhatsApp Template" },
                                                            { value: "webhook", label: "Webhook" },
                                                            { value: "tag_client", label: "Tag Client" },
                                                            { value: "set_property", label: "Set Property" },
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <Form.Item noStyle shouldUpdate>
                                                    {() => {
                                                        const actionType = String(modalForm.getFieldValue("action_type") || "send_whatsapp_template");
                                                        if (actionType === "send_whatsapp_template") {
                                                            return (
                                                                <Space direction="vertical" style={{ width: "100%" }}>
                                                                    <Form.Item name="template_id" label="Template">
                                                                        <Select
                                                                            showSearch
                                                                            optionFilterProp="label"
                                                                            options={templates.map((t: V2TemplateListItemDto) => ({
                                                                                value: t.id,
                                                                                label: `${t.title} (#${t.id})`,
                                                                            }))}
                                                                        />
                                                                    </Form.Item>
                                                                    <Form.Item noStyle shouldUpdate>
                                                                        {() => {
                                                                            const templateId = Number(modalForm.getFieldValue("template_id") || 0);
                                                                            if (!templateId) return null;
                                                                            const selectedTemplate = templates.find((t) => Number(t.id) === templateId);
                                                                            const vars = parseTemplateVariablesSchema(
                                                                                selectedTemplate?.variables_schema_json
                                                                            );
                                                                            return (
                                                                                <div
                                                                                    style={{
                                                                                        background: "#f7f9ff",
                                                                                        border: "1px solid #d8e2ff",
                                                                                        borderRadius: 10,
                                                                                        padding: "10px 12px",
                                                                                        marginTop: -6,
                                                                                    }}
                                                                                >
                                                                                    <div style={{ marginBottom: 8 }}>
                                                                                        <Text strong>Required placeholders</Text>
                                                                                    </div>
                                                                                    {!vars.length ? (
                                                                                        <Text type="secondary">No placeholders required for this template.</Text>
                                                                                    ) : (
                                                                                        <Space direction="vertical" size={6} style={{ width: "100%" }}>
                                                                                            {vars.map((v) => (
                                                                                                <div
                                                                                                    key={v}
                                                                                                    style={{
                                                                                                        display: "flex",
                                                                                                        alignItems: "center",
                                                                                                        justifyContent: "space-between",
                                                                                                        gap: 12,
                                                                                                    }}
                                                                                                >
                                                                                                    <Space direction="vertical" size={0}>
                                                                                                        <Text code>{displayPlaceholderKey(v)}</Text>
                                                                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                                                                            Example: <Text code>{placeholderExampleValue(v)}</Text>
                                                                                                        </Text>
                                                                                                    </Space>
                                                                                                    <Text type="secondary" style={{ fontSize: 12, textAlign: "right" }}>
                                                                                                        {placeholderHelpText(v)}
                                                                                                    </Text>
                                                                                                </div>
                                                                                            ))}
                                                                                        </Space>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        }}
                                                                    </Form.Item>
                                                                    <Form.Item name="missing_variable_policy" label="Missing variable policy">
                                                                        <Select
                                                                            options={[
                                                                                { value: "skip_step", label: "Skip message step" },
                                                                                { value: "fail_run", label: "Fail the run" },
                                                                            ]}
                                                                        />
                                                                    </Form.Item>
                                                                </Space>
                                                            );
                                                        }
                                                        if (actionType === "webhook") {
                                                            return (
                                                                <Space direction="vertical" style={{ width: "100%" }}>
                                                                    <Form.Item name="webhook_url" label="Webhook URL">
                                                                        <Input placeholder="https://example.com/webhooks/vuelve" />
                                                                    </Form.Item>
                                                                    <Form.Item name="webhook_method" label="Method">
                                                                        <Select options={[{ value: "POST" }, { value: "PUT" }, { value: "PATCH" }, { value: "GET" }]} />
                                                                    </Form.Item>
                                                                    <Form.Item name="webhook_body_json" label="Body (JSON)">
                                                                        <Input.TextArea rows={6} placeholder='{"source":"vuelve"}' />
                                                                    </Form.Item>
                                                                </Space>
                                                            );
                                                        }
                                                        if (actionType === "tag_client") {
                                                            return (
                                                                <Space direction="vertical" style={{ width: "100%" }}>
                                                                    <Form.Item name="tag_op" label="Operation">
                                                                        <Select options={[{ value: "add", label: "Add tag" }, { value: "remove", label: "Remove tag" }]} />
                                                                    </Form.Item>
                                                                    <Form.Item name="tag_value" label="Tag">
                                                                        <Input placeholder="vip" />
                                                                    </Form.Item>
                                                                </Space>
                                                            );
                                                        }
                                                        if (actionType === "set_property") {
                                                            return (
                                                                <Space direction="vertical" style={{ width: "100%" }}>
                                                                    <Form.Item name="property_key" label="Property key">
                                                                        <Input placeholder="last_interest" />
                                                                    </Form.Item>
                                                                    <Form.Item name="property_value_text" label="Property value">
                                                                        <Input placeholder="shoes" />
                                                                    </Form.Item>
                                                                </Space>
                                                            );
                                                        }
                                                        return null;
                                                    }}
                                                </Form.Item>
                                            </Space>
                                        );
                                    }
                                    return null;
                                }}
                            </Form.Item>
                        </Form>

                    </>
                ) : null}
            </Modal>

            <Modal
                open={validationDetailsOpen}
                onCancel={() => setValidationDetailsOpen(false)}
                onOk={() => setValidationDetailsOpen(false)}
                title="Validation details"
                width={620}
            >
                <div style={{ maxHeight: 320, overflow: "auto" }}>
                    <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                        {saveValidationErrors.map((err) => (
                            <li key={err}>{err}</li>
                        ))}
                    </ul>
                </div>
            </Modal>

            <Modal
                open={edgeEditOpen}
                onCancel={() => {
                    setEdgeEditOpen(false);
                    setEdgeEditTarget(null);
                }}
                onOk={applyEdgeKeyChange}
                okText="Apply"
                title="Edit path type"
                width={420}
            >
                {edgeEditTarget ? (
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Text type="secondary">
                            Step {edgeEditTarget.fromStepIndex} → Step {edgeEditTarget.toStepIndex}
                        </Text>
                        <Select
                            value={edgeEditKey}
                            onChange={(v) => setEdgeEditKey(String(v))}
                            options={edgeEditTarget.allowedKeys.map((k) => ({ value: k, label: k }))}
                        />
                    </Space>
                ) : null}
            </Modal>

            <Modal
                open={entryGroupModalOpen}
                onCancel={() => {
                    setEntryGroupModalOpen(false);
                    setEditingEntryGroupId(null);
                }}
                footer={null}
                title="Edit condition set"
                width={760}
            >
                {editingEntryGroup ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <Input
                                defaultValue={editingEntryGroup.title}
                                onBlur={(e) => {
                                    const next = String(e.target.value || "").trim();
                                    if (next !== editingEntryGroup.title) {
                                        patchEntryGroupTitle(editingEntryGroup.conditionId, next);
                                    }
                                }}
                            />
                            <Select
                                style={{ width: 170 }}
                                value={
                                    editingEntryGroup.timeframeSeconds && Number(editingEntryGroup.timeframeSeconds) > 0
                                        ? "rolling_window"
                                        : "all_history"
                                }
                                options={[
                                    { value: "all_history", label: "All history" },
                                    { value: "rolling_window", label: "Rolling window" },
                                ]}
                                onChange={(v) => {
                                    if (String(v) === "rolling_window") {
                                        const next =
                                            editingEntryGroup.timeframeSeconds && Number(editingEntryGroup.timeframeSeconds) > 0
                                                ? Number(editingEntryGroup.timeframeSeconds)
                                                : 3600;
                                        patchEntryGroupTimeframe(editingEntryGroup.conditionId, next);
                                    } else {
                                        patchEntryGroupTimeframe(editingEntryGroup.conditionId, null);
                                    }
                                }}
                            />
                            {editingEntryGroup.timeframeSeconds && Number(editingEntryGroup.timeframeSeconds) > 0 ? (
                                <InputNumber
                                    min={1}
                                    step={60}
                                    style={{ width: 120 }}
                                    value={Number(editingEntryGroup.timeframeSeconds)}
                                    onChange={(v) =>
                                        patchEntryGroupTimeframe(
                                            editingEntryGroup.conditionId,
                                            typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 3600
                                        )
                                    }
                                />
                            ) : null}
                            <Button onClick={() => addEntryLeaf(editingEntryGroup.conditionId)}>+ Add rule</Button>
                            {editingEntryGroup.conditionItemId ? (
                                <Button
                                    danger
                                    onClick={async () => {
                                        await removeEntryGroup(editingEntryGroup.conditionItemId);
                                        setEntryGroupModalOpen(false);
                                        setEditingEntryGroupId(null);
                                    }}
                                >
                                    Remove set
                                </Button>
                            ) : null}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {editingEntryGroup.leaves.map((leaf, idx) => (
                                <div
                                    key={`edit-leaf-${leaf.itemId}`}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                        border: "1px solid #edf1fb",
                                        borderRadius: 8,
                                        padding: 8,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "90px 1fr 110px 110px 90px 32px",
                                            gap: 6,
                                            alignItems: "center",
                                        }}
                                    >
                                        <Text type="secondary">Rule {idx + 1}</Text>
                                        <Input
                                            value={leaf.eventName}
                                            onChange={(e) =>
                                                setEntryTreeBundle((prev) => {
                                                    if (!prev) return prev;
                                                    const clone = JSON.parse(JSON.stringify(prev));
                                                    const basic = clone.basicById?.[String(leaf.basicId)];
                                                    if (basic) basic.event_name = e.target.value;
                                                    return clone;
                                                })
                                            }
                                            onBlur={(e) => patchEntryLeaf(leaf.basicId, { event_name: e.target.value.trim() || null })}
                                        />
                                        <Select
                                            value={leaf.aggregation}
                                            options={[{ value: "exists", label: "exists" }, { value: "count", label: "count" }]}
                                            onChange={(v) =>
                                                patchEntryLeaf(leaf.basicId, {
                                                    aggregation: v as any,
                                                    operator: v === "count" ? ">=" : "EXISTS",
                                                    threshold: v === "count" ? 1 : null,
                                                })
                                            }
                                        />
                                        <Select
                                            value={leaf.operator}
                                            options={
                                                leaf.aggregation === "count"
                                                    ? [{ value: ">=" }, { value: ">" }, { value: "<=" }, { value: "<" }, { value: "=" }, { value: "!=" }]
                                                    : [{ value: "EXISTS" }, { value: "NOT_EXISTS" }]
                                            }
                                            onChange={(v) => patchEntryLeaf(leaf.basicId, { operator: v as any })}
                                        />
                                        <InputNumber
                                            disabled={leaf.aggregation !== "count"}
                                            value={leaf.threshold ?? undefined}
                                            onChange={(v) => patchEntryLeaf(leaf.basicId, { threshold: typeof v === "number" ? v : null })}
                                        />
                                        <Button danger onClick={() => removeEntryLeaf(leaf.itemId)}>
                                            x
                                        </Button>
                                    </div>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "130px 150px 1fr 1fr 130px 120px",
                                            gap: 6,
                                            alignItems: "center",
                                        }}
                                    >
                                        <Text type="secondary">Entity scope</Text>
                                        <Select
                                            value={leaf.entityScopeMode}
                                            options={[
                                                { value: "any", label: "Any" },
                                                { value: "run_context", label: "Run context" },
                                                { value: "specific", label: "Specific" },
                                            ]}
                                            onChange={(v) => {
                                                const mode = String(v) as "any" | "run_context" | "specific";
                                                patchEntryLeaf(leaf.basicId, {
                                                    filters_json: buildEntryLeafFiltersJson({
                                                        entityScopeMode: mode,
                                                        entityType: leaf.entityType,
                                                        entityId: leaf.entityId,
                                                        timeScopeMode: leaf.timeScopeMode,
                                                        timeScopeSeconds: leaf.timeScopeSeconds,
                                                    }),
                                                });
                                            }}
                                        />
                                        {leaf.entityScopeMode === "specific" ? (
                                            <>
                                                <Input
                                                    placeholder="entity_type"
                                                    value={leaf.entityType}
                                                    onBlur={(e) =>
                                                        patchEntryLeaf(leaf.basicId, {
                                                            filters_json: buildEntryLeafFiltersJson({
                                                                entityScopeMode: "specific",
                                                                entityType: e.target.value,
                                                                entityId: leaf.entityId,
                                                                timeScopeMode: leaf.timeScopeMode,
                                                                timeScopeSeconds: leaf.timeScopeSeconds,
                                                            }),
                                                        })
                                                    }
                                                    onChange={(e) =>
                                                        setEntryTreeBundle((prev) => {
                                                            if (!prev) return prev;
                                                            const clone = JSON.parse(JSON.stringify(prev));
                                                            const basic = clone.basicById?.[String(leaf.basicId)];
                                                            const cur = parseEntryLeafScopes(basic?.filters_json);
                                                            basic.filters_json = buildEntryLeafFiltersJson({
                                                                entityScopeMode: "specific",
                                                                entityType: e.target.value,
                                                                entityId: cur.entityId,
                                                                timeScopeMode: cur.timeScopeMode,
                                                                timeScopeSeconds: cur.timeScopeSeconds,
                                                            });
                                                            return clone;
                                                        })
                                                    }
                                                />
                                                <Input
                                                    placeholder="entity_id"
                                                    value={leaf.entityId}
                                                    onBlur={(e) =>
                                                        patchEntryLeaf(leaf.basicId, {
                                                            filters_json: buildEntryLeafFiltersJson({
                                                                entityScopeMode: "specific",
                                                                entityType: leaf.entityType,
                                                                entityId: e.target.value,
                                                                timeScopeMode: leaf.timeScopeMode,
                                                                timeScopeSeconds: leaf.timeScopeSeconds,
                                                            }),
                                                        })
                                                    }
                                                    onChange={(e) =>
                                                        setEntryTreeBundle((prev) => {
                                                            if (!prev) return prev;
                                                            const clone = JSON.parse(JSON.stringify(prev));
                                                            const basic = clone.basicById?.[String(leaf.basicId)];
                                                            const cur = parseEntryLeafScopes(basic?.filters_json);
                                                            basic.filters_json = buildEntryLeafFiltersJson({
                                                                entityScopeMode: "specific",
                                                                entityType: cur.entityType,
                                                                entityId: e.target.value,
                                                                timeScopeMode: cur.timeScopeMode,
                                                                timeScopeSeconds: cur.timeScopeSeconds,
                                                            });
                                                            return clone;
                                                        })
                                                    }
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <div />
                                                <div />
                                            </>
                                        )}
                                        <Text type="secondary">Time scope</Text>
                                        <Select
                                            value={leaf.timeScopeMode}
                                            options={[
                                                { value: "inherit", label: "Inherit group" },
                                                { value: "rolling_window", label: "Rolling window" },
                                                { value: "all_history", label: "All history" },
                                            ]}
                                            onChange={(v) => {
                                                const mode = String(v) as "inherit" | "rolling_window" | "all_history";
                                                patchEntryLeaf(leaf.basicId, {
                                                    filters_json: buildEntryLeafFiltersJson({
                                                        entityScopeMode: leaf.entityScopeMode,
                                                        entityType: leaf.entityType,
                                                        entityId: leaf.entityId,
                                                        timeScopeMode: mode,
                                                        timeScopeSeconds: leaf.timeScopeSeconds,
                                                    }),
                                                });
                                            }}
                                        />
                                    </div>
                                    {leaf.timeScopeMode === "rolling_window" ? (
                                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                            <Text type="secondary">Window seconds</Text>
                                            <InputNumber
                                                min={1}
                                                step={60}
                                                value={leaf.timeScopeSeconds ?? 3600}
                                                onChange={(v) =>
                                                    patchEntryLeaf(leaf.basicId, {
                                                        filters_json: buildEntryLeafFiltersJson({
                                                            entityScopeMode: leaf.entityScopeMode,
                                                            entityType: leaf.entityType,
                                                            entityId: leaf.entityId,
                                                            timeScopeMode: "rolling_window",
                                                            timeScopeSeconds:
                                                                typeof v === "number" && Number.isFinite(v) && v > 0
                                                                    ? Math.floor(v)
                                                                    : 3600,
                                                        }),
                                                    })
                                                }
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setEntryGroupModalOpen(false);
                                    setEditingEntryGroupId(null);
                                }}
                            >
                                Done
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Modal>

            {exitConditionId ? (
                <ConditionTreeEditorModal
                    domain={domain}
                    rootConditionId={exitConditionId}
                    open={exitConditionModalOpen}
                    onClose={() => setExitConditionModalOpen(false)}
                    onChanged={() => {
                        reloadPrimaryTriggerState();
                    }}
                />
            ) : null}
        </div>
    );
}
